// src/components/OrderEntry/hooks/useOrderEntryController.js
// =============================================================================
// ORDER ENTRY CONTROLLER HOOK
// =============================================================================
// Purpose:
//  - Move state + effects + handlers out of OrderEntry.jsx
//  - Keep OrderEntry.jsx as a thin render shell (panels + layout)
//  - Preserve existing behavior (no logic changes)
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  bulkSetFreightAccounts,
  checkPoDuplicate,
  checkPoFormatSimilarity,
  clearFreightAccounts,
  fetchShipEtaBatch,
  getAddressesByCustomer,
  getBitrhythmStudies,
  getConfirmToNamesLast365,
  getCustomerNotes,
  getCustomersByZip,
  getHouseAccountReps,
  getMostRecentFob,
  getPartInfo,
  getPartList,
  getSalesReps,
  getShipToAddresses,
  getShipViaCodes,
  getTerms,
  getTziProductQuote,
  listSavedFreightAccounts,
  saveFreightAccount,
} from '../api/orderEntryApi.js';
import { isCompleteDirectShipPatient } from '../utils/directShipAddress.js';
import { getDirectShipFreightDefaults, isTmsPatientShipTo } from '../utils/directShipRules.js';
import { inferCarrierFromFob, isValidFreightAccountForCarrier } from '../utils/freight.js';
import {
  buildFreightAccountPreview,
  getEffectiveFreightAccount,
} from '../utils/freightAccountRows.js';
import inferZeroInsurance from '../utils/insurance.js';
import { formatMoneyInput, formatUSD, parseMoneyNumber, parseQtyInt } from '../utils/parsers.js';
import normalizePostal5, { normalizeUsZip5 } from '../utils/postal.js';

// Normalize unknown inputs to arrays for API responses.
const toArray = (val) => (Array.isArray(val) ? val : []);

// -----------------------------------------------------------------------------
// BLOCK OEC.DIRECT_SHIP.UTIL.0 — Direct-ship patient helpers
// -----------------------------------------------------------------------------
const createEmptyDirectShipPatient = (contextKey = '') => ({
  contextKey,
  studyId: '',
  studyAccessoryKit: '',
  shipToName: '',
  address1: 'OR Residence',
  rawAddress: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  patientPhoneNumber: '',
});

const buildDirectShipContextKey = ({ company, customerNo, shipToCode }) =>
  `${String(company ?? '')
    .trim()
    .toLowerCase()}::${String(customerNo ?? '').trim()}::${String(shipToCode ?? '')
    .trim()
    .toUpperCase()}`;

const isTmsCompanyCode = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase() === 'tms';

const getDefaultFreightPayForCompany = (value) => (isTmsCompanyCode(value) ? 'Free' : 'FC');

const normalizeCompanyCode = (value) => (isTmsCompanyCode(value) ? 'tms' : 'tzi');

const sanitizeTmsLookupRows = (company, rows, keyFn) => {
  const list = toArray(rows);

  if (!isTmsCompanyCode(company)) {
    const seen = new Set();
    return list.filter((row) => {
      const key = keyFn(row);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  const seen = new Set();
  return list.filter((row) => {
    const key = keyFn(row);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const normalizeShipToSearchRows = (rows) =>
  toArray(rows)
    .map((row) => ({
      customerNo: String(row?.customerNo ?? '').trim(),
      shipToCode: String(row?.shipToCode ?? '').trim(),
      shipToName: String(row?.shipToName ?? '').trim(),
      shipToZip: normalizePostal5(row?.shipToZip ?? ''),
      shipToAddress1: String(row?.shipToAddress1 ?? '').trim(),
      shipToAddress2: String(row?.shipToAddress2 ?? '').trim(),
      shipToAddress3: String(row?.shipToAddress3 ?? '').trim(),
      city: String(row?.city ?? '').trim(),
      state: String(row?.state ?? '')
        .trim()
        .toUpperCase(),
    }))
    .filter((row) => row.shipToName && row.shipToZip);

const dedupeShipToSearchRows = (rows) => {
  const seen = new Set();

  return toArray(rows).filter((row) => {
    const key = [row?.customerNo, row?.shipToCode, row?.shipToName, row?.shipToZip]
      .map((value) =>
        String(value ?? '')
          .trim()
          .toUpperCase()
      )
      .join('::');

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const normalizeShipToSearchTerm = (value) =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9&'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getShipToSearchRequiredLength = (value = '') => {
  const normalized = normalizeShipToSearchTerm(value).replace(/\s+/g, '');
  return /[&']/.test(normalized) ? 5 : 4;
};

const isShipToSearchLongEnough = (value = '') => {
  const normalized = normalizeShipToSearchTerm(value).replace(/\s+/g, '');
  return normalized.length >= getShipToSearchRequiredLength(normalized);
};

const toPostal5 = (value) =>
  String(value ?? '')
    .replace(/[^0-9]/g, '')
    .slice(0, 5);

// BLOCK OEC.POFORMAT.001 — PO format warning threshold
// Change this from 50 to 80 depending how strict you want the warning.
const PO_FORMAT_WARNING_THRESHOLD = 80;

// 50 standard US state abbreviations (intentional exclusion: DC/territories).
const US_STANDARD_STATE_CODES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
]);

export default function useOrderEntryController({ userEmail, initialCompany = 'tzi' } = {}) {
  // ---------------------------------------------------------------------------
  // BLOCK OEC.0.0 — ETA request control refs (debounce + sequencing)
  // ---------------------------------------------------------------------------
  const etaRequestSeqRef = useRef(0);
  const etaDebounceTimerRef = useRef(null);

  // ===========================================================================
  // BLOCK OEC.1 — HEADER / CONTEXT STATE (Company + ZIP/Customer/Address selection)
  // ===========================================================================
  const defaultCompany = normalizeCompanyCode(initialCompany);
  const [company, setCompany] = useState(defaultCompany);

  const frtAccountEditStartValueRef = useRef('');
  const [zip, setZip] = useState('');
  const [zipForSearch, setZipForSearch] = useState('');
  // Address selection can change the destination ZIP without restarting customer search.
  const addressDisplayZipRef = useRef(null);

  const [state, setState] = useState('');
  const [city, setCity] = useState('');

  const [masId, setMasId] = useState('');
  const [shipId, setShipId] = useState('');

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const [shipToSearchRows, setShipToSearchRows] = useState([]);
  const [shipToSearchLoading, setShipToSearchLoading] = useState(false);
  const [shipToSearchError, setShipToSearchError] = useState('');
  const [customerListFocusNonce, setCustomerListFocusNonce] = useState(0);
  const shipToSearchRequestSeqRef = useRef(0);
  const customerSelectRequestSeqRef = useRef(0);
  const shouldFocusCustomerListRef = useRef(false);
  const requireManualStreetSelectionRef = useRef(false);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(null);

  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  const [lastSelectedAddress, setLastSelectedAddress] = useState(null);

  const [billTo, setBillTo] = useState(null);
  const [notes, setNotes] = useState([]);

  // =========================================================================
  // BLOCK OEC.1A — Customer / Ship-To display fields (LEGACY REQUIRED)
  // =========================================================================
  const [customerName, setCustomerName] = useState('');
  const [shipAddress1, setShipAddress1] = useState('');
  const [shipAddress2, setShipAddress2] = useState('');

  // =========================================================================
  // BLOCK OEC.1A1 — TMS direct-ship patient override state
  // =========================================================================
  const [directShipPatient, setDirectShipPatient] = useState(() => createEmptyDirectShipPatient());
  const [directShipDialogOpen, setDirectShipDialogOpen] = useState(false);
  const [directShipStudies, setDirectShipStudies] = useState([]);
  const [directShipStudiesLoading, setDirectShipStudiesLoading] = useState(false);
  const [directShipStudiesError, setDirectShipStudiesError] = useState('');

  const directShipPatientActive = isTmsPatientShipTo(company, shipId);
  const directShipPatientComplete = useMemo(
    () => directShipPatientActive && isCompleteDirectShipPatient(directShipPatient),
    [directShipPatientActive, directShipPatient]
  );
  const directShipFreightDefaults = useMemo(
    () => getDirectShipFreightDefaults(company, shipId),
    [company, shipId]
  );

  // ---------------------------------------------------------------------------
  // BLOCK OEC.1A2 — Preserve the selected Sage ShipToCode during P override
  // ---------------------------------------------------------------------------
  const selectedAddressShipId = useMemo(() => {
    if (selectedAddressIndex === null || selectedAddressIndex === undefined) {
      return '';
    }

    return String(addresses?.[selectedAddressIndex]?.shipToCode ?? '')
      .trim()
      .toUpperCase();
  }, [addresses, selectedAddressIndex]);

  const selectedAddressFacilityZip = useMemo(() => {
    if (selectedAddressIndex === null || selectedAddressIndex === undefined) {
      return '';
    }

    return String(addresses?.[selectedAddressIndex]?.zipCode ?? '').trim();
  }, [addresses, selectedAddressIndex]);

  const [user, setUser] = useState('');

  // ===========================================================================
  // BLOCK OEC.1B — HOUSE FLAGS (Credit Hold, etc.)
  // ===========================================================================
  const [creditHold, setCreditHold] = useState(false);

  // BLOCK OEC.1C — Logged-in user (Excel parity)
  // =========================================================================
  useEffect(() => {
    if (user) {
      return;
    }

    // Priority 1: Use userEmail prop from Auth0 (most reliable)
    if (userEmail) {
      // Strip domain from email: "johnl@tzmedical.com" → "johnl"
      const emailParts = String(userEmail).trim().split('@');
      const username = emailParts[0] || String(userEmail).trim();
      setUser(username);
      return;
    }

    // Priority 2: Prefer non-underscored globals; allow legacy if your shell still sets it
    const w = typeof window !== 'undefined' ? window : null;

    const fromStorage =
      localStorage.getItem('username') ||
      localStorage.getItem('loginId') ||
      localStorage.getItem('user') ||
      localStorage.getItem('userid') ||
      sessionStorage.getItem('username') ||
      sessionStorage.getItem('loginId') ||
      '';

    const direct = w?.APP_USER || fromStorage || '';

    const legacy = direct ? '' : w?.__APP_USER__ || '';

    setUser(String(direct || legacy || '').trim());
  }, [user, userEmail]);

  // ===========================================================================
  // BLOCK OEC.2 — ORDER HEADER INPUTS (PO / ConfirmTo / Phone / Receiving Line)
  // ===========================================================================
  // Confirm To:
  // - Always defaults to "Purchasing"
  // - Options are loaded from last 365 days for current MASID + ShipTo
  const [receivingLine, setReceivingLine] = useState('');
  const [confirmTo, setConfirmTo] = useState('Purchasing');
  const [confirmToOptions, setConfirmToOptions] = useState(['Purchasing']);
  const [confirmToLoading, setConfirmToLoading] = useState(false);

  const [phone, setPhone] = useState('');
  const [poNumber, setPoNumber] = useState('');

  // ===========================================================================
  // BLOCK OEC.3 — SPECIAL ORDER POPUP STATE
  // ===========================================================================
  const [specialOpen, setSpecialOpen] = useState(false);
  const [specialAck, setSpecialAck] = useState(false);

  // ===========================================================================
  // BLOCK OEC.4A — Freight account manual-edit guard
  // ===========================================================================
  const [frtAccountUserEditing, setFrtAccountUserEditing] = useState(false);
  const [carrier, setCarrier] = useState('FDX');
  const [frtPay, setFrtPay] = useState(() => getDefaultFreightPayForCompany(defaultCompany));
  const [shipVia, setShipVia] = useState('');

  const DEFAULT_SHIPVIA = useMemo(() => {
    if (directShipFreightDefaults?.shipVia) {
      return directShipFreightDefaults.shipVia;
    }

    if (directShipFreightDefaults?.carrier === 'USPS') {
      return 'USPSG';
    }

    const isCollect = frtPay === 'FC';

    if (carrier === 'UPS') {
      return isCollect ? 'UPS GD (FC)' : 'UPS GD';
    }

    // FedEx
    return isCollect ? 'FDX-GND-FC' : 'FDX-GND';
  }, [carrier, directShipFreightDefaults, frtPay]);

  const [shipViaInitialized, setShipViaInitialized] = useState(false);

  const [shipCodeRows, setShipCodeRows] = useState([]); // raw rows from DB
  const [shipEtaMap, setShipEtaMap] = useState({}); // { [shipViaCode]: { label, date, ... } }

  const carrierDb = carrier;
  const fcDb = frtPay === 'FC' ? 1 : 0;

  const [frtAccount, setFrtAccount] = useState('');
  const [contractCode, setContractCode] = useState('');
  const [contractNo, setContractNo] = useState('');

  const [splitCommission, setSplitCommission] = useState(false);

  // ========================================================================
  // BLOCK OEC.STATE.REVREQ.0 — Revision Requested (Badge 6)
  // - Forced ON when HouseAccounts.rev_req_chk === 1 (per requirement)
  // - We do NOT auto-force it OFF when flag is 0 (user choice persists)
  // ========================================================================
  const [revReq, setRevReq] = useState(false);

  // ============================================================================
  // BLOCK OEC.REF.REVREQ.FORCE.0 — One-time force-on guard per customer context
  // - Applies revReq=true once per (company+masId+shipId) when revReqChk=1
  // - Prevents repeated forcing across re-renders
  // ============================================================================
  const revReqForcedAppliedRef = useRef(false);

  // ============================================================================
  // BLOCK OEC.REF.REVREQ.FORCE.1 — Reset guard when customer context changes
  // ============================================================================
  useEffect(() => {
    revReqForcedAppliedRef.current = false;

    // If we leave customer context, clear UI checkbox (safe default)
    if (!masId || !shipId) {
      setRevReq(false);
    }
  }, [company, masId, shipId]);

  // ========================================================================
  // BLOCK OEC.REF.HOUSE.IGNORE.0 — HouseAccounts split=2 IGNORE guard
  // - When IGNORE, House Account must NEVER be auto-checked by notes/flags parsing
  // ========================================================================
  const houseAccountIgnoreRef = useRef(false);

  // ========================================================================
  // OEC.STATE.SPEC.0 — Special order type flags + customer note
  // - We must NOT collapse DS/MO/BL into BO.
  // - Customer note flows to Sugar note + submit-order /C rows.
  // ========================================================================
  const [specOrderBo, setSpecOrderBo] = useState(false);
  const [specOrderMo, setSpecOrderMo] = useState(false);
  const [specOrderDs, setSpecOrderDs] = useState(false);
  const [specOrderBl, setSpecOrderBl] = useState(false);
  const [specOrderCustomerNote, setSpecOrderCustomerNote] = useState('');

  const [houseAccount, setHouseAccount] = useState(isTmsCompanyCode(defaultCompany));
  const [omitCommentCodes, setOmitCommentCodes] = useState(isTmsCompanyCode(defaultCompany));
  const [customCommentsText, setCustomCommentsText] = useState('');

  const [internationalSale, setInternationalSale] = useState(false);
  const [zeroInsurance, setZeroInsurance] = useState(false);
  const [savedFreightRows, setExistingFreightAccounts] = useState([]);
  const [freightDataKey, setFreightDataKey] = useState('');
  const [freightShipTos, setFreightShipTos] = useState([]);
  const [freightLoadError, setFreightLoadError] = useState('');
  const [freightReloadNonce, setFreightReloadNonce] = useState(0);
  const [freightSaveContext, setFreightSaveContext] = useState(null);
  const freightRequestSeqRef = useRef(0);
  const freightMutationBusyRef = useRef(false);
  const [freightSaveOpen, setFreightSaveOpen] = useState(false);
  const [freightSaveScope, setFreightSaveScope] = useState('shipto');
  const [freightSaveError, setFreightSaveError] = useState(null);
  const [freightSaveBusy, setFreightSaveBusy] = useState(false);
  const freightContextKey = JSON.stringify([company, masId, shipId, carrier]);
  const freightQueryKey = JSON.stringify([freightContextKey, freightReloadNonce, freightSaveOpen]);
  const freightLiveContextRef = useRef(freightContextKey);
  useEffect(() => {
    freightLiveContextRef.current = freightContextKey;
  }, [freightContextKey]);
  const freightAccountsReady = freightDataKey === freightQueryKey;
  const freightAccountsLoading = Boolean(
    masId && carrier && !freightAccountsReady && !freightLoadError
  );
  const existingFreightAccounts = useMemo(
    () => (freightAccountsReady ? savedFreightRows : []),
    [freightAccountsReady, savedFreightRows]
  );
  const retryFreightAccounts = useCallback(() => {
    setFreightLoadError('');
    setFreightReloadNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (freightSaveContext && freightSaveContext.key !== freightContextKey) {
      setFreightSaveOpen(false);
      setFreightSaveContext(null);
    }
  }, [freightContextKey, freightSaveContext]);

  // =========================================================================
  // BLOCK OEC.4B — Freight account removal (when user clears the field)
  // =========================================================================
  const [freightRemoveOpen, setFreightRemoveOpen] = useState(false);
  const [freightRemoveScope, setFreightRemoveScope] = useState('shipto');
  const [freightRemoveError, setFreightRemoveError] = useState(null);
  const [freightRemoveBusy, setFreightRemoveBusy] = useState(false);

  // Auto-populate International Sale for non-US state/province values.
  useEffect(() => {
    const normalizedState = String(state || '')
      .trim()
      .toUpperCase();

    if (!normalizedState) {
      return;
    }

    if (!US_STANDARD_STATE_CODES.has(normalizedState) && !internationalSale) {
      setInternationalSale(true);
    }
  }, [state, internationalSale]);

  // ===========================================================================
  // BLOCK OEC.5 — PART LOOKUP STATE
  // ===========================================================================
  const [partNo, setPartNo] = useState('');
  const [partInput, setPartInput] = useState('');
  const [partOptions, setPartOptions] = useState([]);

  const [partLoading, setPartLoading] = useState(false);
  const [partError, setPartError] = useState(null);
  const [productQuote, setProductQuote] = useState(null);
  const [productQuoteDialogOpen, setProductQuoteDialogOpen] = useState(false);
  const [productQuoteDefaultUsePrice, setProductQuoteDefaultUsePrice] = useState(false);

  const clearProductQuote = useCallback(() => {
    setProductQuote(null);
    setProductQuoteDialogOpen(false);
    setProductQuoteDefaultUsePrice(false);
  }, []);

  const [contractRows, setContractRows] = useState([]);
  const [lastPriceRow, setLastPriceRow] = useState(null);
  const [specialNote, setSpecialNote] = useState(null);

  const [uom, setUom] = useState('');
  const [qty, setQty] = useState('');
  const [price, setPrice] = useState('');
  const [priceAutoSource, setPriceAutoSource] = useState('none'); // last_order | contract | none

  // ===========================================================================
  // BLOCK OEC.5A — SALES REPS STATE
  // ===========================================================================
  const [salesReps, setSalesReps] = useState({ rep: [], mngr: [], tsr: [] });
  const [houseAccountReps, setHouseAccountReps] = useState({
    rep1: '',
    rep2: '',
    rep1Name: '',
    rep2Name: '',
    split: 0, // 0=house, 1=split, 2=ignore
    revReqChk: 0, // 1 => frontend auto-check Revision Requested
  });

  // Source-of-truth slots for legacy submit (max 5)
  const [repSlots, setRepSlots] = useState([]); // [{ div:'00', rep:'0118' }, ...]

  // Normalize whatever salesReps.rep returns into slots
  const normalizeRepSlots = useCallback((repList) => {
    const src = Array.isArray(repList) ? repList : [];
    const slots = [];

    src.forEach((item) => {
      if (slots.length >= 5) {
        return;
      }

      // rep list is often an array of strings like ["0118","0057","CVIZ"]
      if (typeof item === 'string' || typeof item === 'number') {
        const rep = String(item).trim();
        if (rep) {
          slots.push({ div: '00', rep });
        }
        return;
      }

      // sometimes objects (from API: { no, name, email } or { rep, div })
      if (item && typeof item === 'object') {
        // Try multiple property names for rep number
        const rep = String(item.rep ?? item.no ?? item.repId ?? item.rep_id ?? '').trim();
        if (!rep) {
          return;
        }
        const div = String(item.div ?? item.division ?? item.divisionNo ?? '00').trim() || '00';
        slots.push({ div, rep });
      }
    });

    return slots;
  }, []);

  const legacyRepFields = useMemo(() => {
    const s = Array.isArray(repSlots) ? repSlots : [];
    const pick = (i) => (s[i] ? s[i] : null);

    const s1 = pick(0);
    const s2 = pick(1);
    const s3 = pick(2);
    const s4 = pick(3);
    const s5 = pick(4);

    return {
      divrep1: s1 ? String(s1.div ?? '00').trim() : '',
      rep1: s1 ? String(s1.rep ?? '').trim() : '',
      divrep2: s2 ? String(s2.div ?? '00').trim() : '',
      rep2: s2 ? String(s2.rep ?? '').trim() : '',
      divrep3: s3 ? String(s3.div ?? '00').trim() : '',
      rep3: s3 ? String(s3.rep ?? '').trim() : '',
      divrep4: s4 ? String(s4.div ?? '00').trim() : '',
      rep4: s4 ? String(s4.rep ?? '').trim() : '',
      divrep5: s5 ? String(s5.div ?? '00').trim() : '',
      rep5: s5 ? String(s5.rep ?? '').trim() : '',
    };
  }, [repSlots]);

  // ===========================================================================
  // BLOCK OEC.5B — TERMS STATE
  // ===========================================================================
  const [termsValue, setTermsValue] = useState('');
  const [termsOptions, setTermsOptions] = useState([]); // [{ value, label }]
  const [preferredTermsCode, setPreferredTermsCode] = useState('');

  // ===========================================================================
  // BLOCK OEC.5C — Price mismatch warnings (UI)
  // ===========================================================================
  const [priceWarningText, setPriceWarningText] = useState('');

  // ===========================================================================
  // BLOCK OEC.6 — Refs
  // ===========================================================================
  const zipRef = useRef(null);
  const poRef = useRef(null);
  const partRef = useRef(null);
  const qtyRef = useRef(null);

  const focusPoNumber = useCallback(() => {
    if (poRef.current) {
      poRef.current.focus();
    }
  }, []);

  const focusPartNo = useCallback(() => {
    if (partRef.current) {
      partRef.current.focus();
    }
  }, []);

  const focusQty = useCallback(() => {
    if (qtyRef.current) {
      qtyRef.current.focus();
      if (typeof qtyRef.current.select === 'function') {
        qtyRef.current.select();
      }
    }
  }, []);

  // ============================================================================
  // BLOCK OEC.6A — Refs to avoid effect dependency loops
  // ============================================================================
  const frtAccountRef = useRef('');
  const frtAccountEditingRef = useRef(false);

  useEffect(() => {
    frtAccountRef.current = String(frtAccount ?? '');
  }, [frtAccount]);

  useEffect(() => {
    frtAccountEditingRef.current = Boolean(frtAccountUserEditing);
  }, [frtAccountUserEditing]);

  // ============================================================================
  // BLOCK OEC.6B — ETA refresh guards (refs)
  // ============================================================================
  const skipNextEtaRefreshRef = useRef(false);
  const lastEtaKeyRef = useRef('');
  const etaInFlightRef = useRef(false);

  // BLOCK OEC.6C — Freight autopilot guards
  // ============================================================================
  // manualFreightOverrideRef: legacy "hard stop" guard when user explicitly changes
  // carrier or freight pay. We keep this for backward compatibility.
  const manualFreightOverrideRef = useRef(false);

  // Fine-grained "user touched" guards so autopilot can still run when safe.
  // - frtPayUserTouchedRef: user manually changed freight pay for the current part/context
  // - frtAccountUserTouchedRef: user manually edited the freight account field
  const frtPayUserTouchedRef = useRef(false);
  const frtAccountUserTouchedRef = useRef(false);

  // Helper: reset all freight override/touched guards (called on part lookup + context resets)
  const resetFreightAutopilotGuards = useCallback(() => {
    manualFreightOverrideRef.current = false;
    frtPayUserTouchedRef.current = false;
    frtAccountUserTouchedRef.current = false;
  }, []);

  // ============================================================================
  // BLOCK OEC.6D — $0 Insurance manual override guard
  // ============================================================================
  const zeroInsuranceManualRef = useRef(false);

  // ============================================================================
  // BLOCK OEC.6E — Carrier ref (avoid FOB effect re-run when carrier changes)
  // ============================================================================
  const carrierRef = useRef('FDX');

  useEffect(() => {
    carrierRef.current = String(carrier ?? '').trim();
  }, [carrier]);

  // ===========================================================================
  // BLOCK OEC.7 — Derived values
  // ===========================================================================
  const orderTotalText = useMemo(() => {
    const q = parseQtyInt(qty);
    const p = parseMoneyNumber(price);
    if (!q || !p) {
      return ' ';
    }
    return `Order Total: ${formatUSD(q * p)}`;
  }, [qty, price]);

  // ===========================================================================
  // BLOCK OEC.8.C — ZIP setter (postal lookup normalization)
  // ===========================================================================
  const setZipNormalized = useCallback((val) => {
    setZip(normalizePostal5(val));
  }, []);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.1 — Load shipvia rows (carrier + frtPay + international)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const normalizeCountry = (v) => {
      const s = String(v || '')
        .trim()
        .toUpperCase();
      return s === 'USA' ? 'US' : s;
    };

    const run = async () => {
      try {
        // TZ ships from US
        const shipFromCountry = 'US';

        // Derive ship-to country from selected address (default US)
        const shipToCountry =
          selectedAddressIndex != null && addresses?.[selectedAddressIndex]?.countryCode
            ? normalizeCountry(addresses[selectedAddressIndex].countryCode)
            : 'US';

        const rows = await getShipViaCodes({
          carrier: carrierDb,
          fc: fcDb,
          shipFromCountry,
          shipToCountry,
        });

        if (!cancelled) {
          setShipCodeRows(toArray(rows));
        }
      } catch {
        if (!cancelled) {
          setShipCodeRows([]);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [carrierDb, fcDb, addresses, selectedAddressIndex]);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.2 — Build shipViaList (dedupe by code)
  // ---------------------------------------------------------------------------
  const shipViaList = useMemo(() => {
    const codeMap = new Map();

    (shipCodeRows || []).forEach((r) => {
      const code = String(r?.code ?? '').trim();
      if (!code) {
        return;
      }

      if (!codeMap.has(code)) {
        codeMap.set(code, {
          code,
          description: String(r?.description ?? '').trim(),
        });
      }
    });

    return Array.from(codeMap.values());
  }, [shipCodeRows]);

  const pickDefaultShipViaCode = useCallback(
    (list) => {
      const rows = Array.isArray(list) ? list : [];
      if (rows.length === 0) {
        return '';
      }

      const codes = rows.map((item) => String(item?.code ?? '').trim()).filter(Boolean);

      const upperRows = codes.map((code) => ({ code, upper: code.toUpperCase() }));
      const isCollect = frtPay === 'FC';

      const findExact = (value) => upperRows.find((row) => row.upper === value)?.code || '';
      const findPred = (pred) => upperRows.find((row) => pred(row.upper))?.code || '';

      if (directShipFreightDefaults?.shipVia) {
        return directShipFreightDefaults.shipVia;
      }

      if (directShipFreightDefaults?.carrier === 'USPS') {
        return findExact('USPSG') || findPred((u) => u.includes('USPSG')) || rows[0]?.code || '';
      }

      if (carrier === 'UPS') {
        if (isCollect) {
          return (
            findExact('UPS GD (FC)') ||
            findPred(
              (u) =>
                u.includes('UPS') && u.includes('FC') && (u.includes('GD') || u.includes('GROUND'))
            ) ||
            findPred((u) => u.includes('UPS') && u.includes('FC')) ||
            codes[0]
          );
        }

        return (
          findExact('UPS - GROUND') ||
          findExact('UPS GD') ||
          findPred(
            (u) =>
              u.includes('UPS') && (u.includes('GROUND') || u.includes('GD')) && !u.includes('FC')
          ) ||
          findPred((u) => u.includes('UPS') && !u.includes('FC')) ||
          codes[0]
        );
      }

      if (carrier === 'FDX') {
        if (isCollect) {
          return (
            findExact('FDX-GND-FC') ||
            findPred((u) => u.includes('FDX') && u.includes('GND') && u.includes('FC')) ||
            findPred((u) => u.includes('FDX') && u.includes('GND')) ||
            codes[0]
          );
        }

        return (
          findExact('FDX-GND') ||
          findPred((u) => u.includes('FDX') && u.includes('GND') && !u.includes('FC')) ||
          findPred((u) => u.includes('FDX') && u.includes('GND')) ||
          codes[0]
        );
      }

      return (
        upperRows.find((row) => row.upper === DEFAULT_SHIPVIA)?.code ||
        findPred((u) => u.includes('GND') && !u.includes('FC')) ||
        codes[0]
      );
    },
    [carrier, directShipFreightDefaults, frtPay, DEFAULT_SHIPVIA]
  );

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.2A — ETA coordination reset when freight mode/carrier changes (NO SHIPVIA WIPE)
  // Why:
  //  - We must NOT clear shipVia on frtPay/carrier change, because that causes the UI to
  //    snap back to DEFAULT_SHIPVIA (FDX-GND/FDX-GND-FC) even when the user selected 2D.
  // Rule:
  //  - Preserve shipVia and shipViaInitialized.
  //  - Let BLOCK OEC.8.5 validate shipVia against the new shipViaList and only fallback
  //    if the current shipVia becomes invalid.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Reset ETA coordination so next stable shipViaList triggers a fresh ETA call
    lastEtaKeyRef.current = '';
    etaInFlightRef.current = false;
    skipNextEtaRefreshRef.current = false;

    // Optional: keeps dropdown stable (it will refill when next ETA completes)
    setShipEtaMap({});
  }, [frtPay, carrier]);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.3 — Postal normalizer + derived ship-to postal
  // ---------------------------------------------------------------------------
  const shipToPostalDerived = useMemo(() => {
    if (directShipPatientActive && directShipPatientComplete) {
      return normalizeUsZip5(directShipPatient.zip);
    }

    const addrZip =
      selectedAddressIndex != null && addresses?.[selectedAddressIndex]?.zipCode
        ? addresses[selectedAddressIndex].zipCode
        : '';
    return normalizeUsZip5(addrZip || zip);
  }, [
    addresses,
    directShipPatient.zip,
    directShipPatientActive,
    directShipPatientComplete,
    selectedAddressIndex,
    zip,
  ]);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.4A — Frontend shipDate cutoff (America/Los_Angeles, 2:30pm)
  // Purpose:
  //  - After 2:30pm LA, send shipDate=tomorrow so backend cache doesn't store
  //    a "today-anchored" transit-days result.
  // Notes:
  //  - Backend must honor body.shipDate (see eta.js override).
  // ---------------------------------------------------------------------------
  function shipDateWithCutoffLAFE() {
    const now = new Date();

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const get = (t) => parts.find((p) => p.type === t)?.value;

    const yyyy = Number(get('year'));
    const mm = Number(get('month'));
    const dd = Number(get('day'));
    const hour = Number(get('hour'));
    const minute = Number(get('minute'));

    const pastCutoff = hour > 14 || (hour === 14 && minute >= 30);

    // Build date at UTC midnight of the LA-local calendar day (matches backend behavior)
    let d = new Date(Date.UTC(yyyy, mm - 1, dd));
    if (pastCutoff) {
      d = new Date(d.getTime() + 24 * 60 * 60 * 1000);
    }

    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.4 — Fetch ship ETA map (UPS/FDX only; cache-first backend)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    // Small debounce helps FedEx sandbox “GENERIC.ERROR” on rapid toggle
    const debounceMs = carrier === 'FDX' ? 250 : 0;

    // Request sequencing prevents stale responses from overwriting the *current* shipVia list
    // NOTE: add these refs near your other refs at top-level of the hook:
    //   const etaRequestSeqRef = useRef(0);
    //   const etaDebounceTimerRef = useRef(null);
    etaRequestSeqRef.current += 1;
    const seq = etaRequestSeqRef.current;

    const run = async () => {
      try {
        // Guard: basic required fields
        if (!masId || !shipId) {
          return;
        }

        // Guard: shipToPostal must be a real 5-digit zip
        const shipToPostal5 = String(shipToPostalDerived || '').trim();
        if (!/^\d{5}$/.test(shipToPostal5)) {
          return;
        }

        // Guard: shipViaList must exist and have items
        if (!Array.isArray(shipViaList) || shipViaList.length === 0) {
          return;
        }

        // Guard: only UPS + FDX drive ETA calls
        let prefix = '';
        if (carrier === 'FDX') {
          prefix = 'FDX';
        } else if (carrier === 'UPS') {
          prefix = 'UPS';
        } else {
          return;
        }

        // Extract shipViaCodes for CURRENT carrier only (dropdown options drive what we ask ETA for)
        const allCodes = shipViaList.map((x) => x?.code).filter(Boolean);
        const shipViaCodes = allCodes.filter((c) => String(c).toUpperCase().startsWith(prefix));
        if (shipViaCodes.length === 0) {
          return;
        }

        if (skipNextEtaRefreshRef.current) {
          skipNextEtaRefreshRef.current = false;
          return;
        }

        const shipFromPostal = '97062';

        const key = JSON.stringify({
          company,
          masId,
          shipId,
          carrier,
          frtPay,
          shipToPostal5,
          shipViaCodes: [...shipViaCodes].sort(),
        });

        if (lastEtaKeyRef.current === key) {
          return;
        }
        lastEtaKeyRef.current = key;

        // Mark in-flight (but do NOT block subsequent calls; sequencing handles stale responses)
        etaInFlightRef.current = true;

        const payload = {
          company,
          customerNo: masId,
          shipToCode: shipId,
          carrier,
          frtPay,
          shipViaCodes,

          // NEW: explicit shipDate so backend cache aligns with cutoff behavior
          shipDate: shipDateWithCutoffLAFE(),

          shipFrom: { postalCode: shipFromPostal, countryCode: 'US' },
          shipTo: { postalCode: shipToPostal5, countryCode: 'US' },
          zip: shipToPostal5,
        };

        const res = await fetchShipEtaBatch(payload);
        const nextEtaMap = res?.etaMap && typeof res.etaMap === 'object' ? res.etaMap : res;

        // Only apply if this is still the latest request sequence
        if (!cancelled && seq === etaRequestSeqRef.current) {
          setShipEtaMap(nextEtaMap && typeof nextEtaMap === 'object' ? nextEtaMap : {});
        }
      } catch {
        // Keep previous shipEtaMap to avoid blanking the dropdown on transient errors
      } finally {
        if (seq === etaRequestSeqRef.current) {
          etaInFlightRef.current = false;
        }
      }
    };

    // Debounce scheduling
    if (etaDebounceTimerRef.current) {
      clearTimeout(etaDebounceTimerRef.current);
    }
    etaDebounceTimerRef.current = setTimeout(() => {
      run();
    }, debounceMs);

    return () => {
      cancelled = true;
      if (etaDebounceTimerRef.current) {
        clearTimeout(etaDebounceTimerRef.current);
      }
    };
  }, [carrier, company, frtPay, masId, shipId, shipToPostalDerived, shipViaList]);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.8.5 — ShipVia selection behavior
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!Array.isArray(shipViaList) || shipViaList.length === 0) {
      if (!shipVia && DEFAULT_SHIPVIA) {
        setShipVia(DEFAULT_SHIPVIA);
      }
      return;
    }

    const nextDefault = pickDefaultShipViaCode(shipViaList);

    if (!shipViaInitialized) {
      setShipVia(nextDefault || shipViaList[0].code);
      setShipViaInitialized(true);
      return;
    }

    if (shipVia && shipViaList.some((item) => item.code === shipVia)) {
      return;
    }

    setShipVia(nextDefault || DEFAULT_SHIPVIA || shipViaList[0].code);
  }, [shipViaList, shipVia, shipViaInitialized, pickDefaultShipViaCode, DEFAULT_SHIPVIA]);

  // ===========================================================================
  // BLOCK OEC.9 — EFFECTS: Contract code auto-select
  // ===========================================================================
  useEffect(() => {
    if (!Array.isArray(contractRows) || contractRows.length === 0) {
      setContractCode('');
      setContractNo('');
      return;
    }

    const first = contractRows[0];
    const firstCode = String(first?.contractCode ?? '').trim();
    const firstNo = String(first?.contractNo ?? '').trim();
    const next = firstCode || firstNo;

    setContractCode((prev) => {
      const prevTrim = String(prev ?? '').trim();
      if (!prevTrim) {
        return next;
      }

      const valid = contractRows.some((r) => String(r?.contractCode ?? '').trim() === prevTrim);
      return valid ? prevTrim : next;
    });

    // Also set contractNo when contractRows change
    setContractNo(firstNo);
  }, [contractRows]);

  // ============================================================================
  // BLOCK OEC.9A — Contract freight intent (simple)
  // ============================================================================
  const contractWantsFree = useMemo(() => {
    if (!Array.isArray(contractRows) || contractRows.length === 0) {
      return false;
    }
    const v = String(contractRows[0]?.freeFreight ?? '')
      .trim()
      .toLowerCase();
    return v.includes('free');
  }, [contractRows]);

  // ============================================================================
  // BLOCK OEC.9B — EFFECT: Freight autopilot (Free / FC / PPA) + saved acct hydrate
  // ----------------------------------------------------------------------------
  // Source-of-truth order (unless user-touched guards block it):
  //   1) If contract indicates Free (including thresholds) => frtPay = 'Free'
  //   2) Else if a freight account exists (saved or entered) => frtPay = 'FC'
  //   3) Else => frtPay = 'PPA'
  //
  // Guards:
  //   - If user touched frtPay OR manual override => do not change frtPay
  //   - If user touched frtAccount OR editing => do not overwrite frtAccount
  // ============================================================================
  useEffect(() => {
    // -------------------------------------------------------------------------
    // BLOCK OEC.9B.DIRECT_SHIP.0 — TMS patient mode owns the freight defaults
    // -------------------------------------------------------------------------
    if (directShipFreightDefaults) {
      if (frtAccount) {
        setFrtAccount('');
      }

      if (frtPay !== directShipFreightDefaults.frtPay) {
        setFrtPay(directShipFreightDefaults.frtPay);
      }

      return;
    }

    // -----------------------------
    // 1) Determine "saved" account
    // -----------------------------
    if (!freightAccountsReady) {
      return;
    }
    const shipCode = String(shipId ?? '')
      .trim()
      .toUpperCase();
    const rows = Array.isArray(existingFreightAccounts) ? existingFreightAccounts : [];

    const shipRow = shipCode
      ? rows.find((r) => String(r?.shipToCode ?? '').trim() === shipCode)
      : null;
    const customerRow = rows.find((r) => String(r?.shipToCode ?? '').trim() === '');
    const hasSavedOverrideRow = Boolean(shipRow || customerRow);

    const savedAcct = getEffectiveFreightAccount(rows, shipCode);

    const acctNow = String(frtAccount ?? '').trim();

    // Hydrate the freight account from saved rows ONLY when:
    //  - we have a saved acct
    //  - the saved value differs (including a stale FOB-derived value)
    //  - user has NOT touched the field and is not actively editing it
    if (
      savedAcct &&
      savedAcct !== acctNow &&
      !frtAccountUserEditing &&
      !frtAccountUserTouchedRef.current
    ) {
      setFrtAccount(savedAcct);
    }

    // A stored blank row is an explicit override and must clear any FOB-derived value.
    if (
      hasSavedOverrideRow &&
      !savedAcct &&
      acctNow &&
      !frtAccountUserEditing &&
      !frtAccountUserTouchedRef.current
    ) {
      setFrtAccount('');
    }

    // Recompute after potential hydration (keep local "effective" value)
    const effectiveAcct = frtAccountUserTouchedRef.current
      ? acctNow
      : String(hasSavedOverrideRow ? savedAcct : acctNow).trim();
    const hasAcct = Boolean(effectiveAcct);

    // -----------------------------
    // 2) Determine "contract is free now"
    // -----------------------------
    const first = Array.isArray(contractRows) && contractRows.length > 0 ? contractRows[0] : null;
    const freightValue = String(first?.freeFreight ?? '').trim();
    const freightLower = freightValue.toLowerCase();

    const qtyNum = parseQtyInt(qty);
    const priceNum = parseMoneyNumber(price);
    const orderTotal = qtyNum && priceNum ? qtyNum * priceNum : 0;
    const uomUpper = String(uom ?? '')
      .trim()
      .toUpperCase();

    // contractFreeNow:
    //  - plain "free" => true
    //  - ">10 BX" => true only if BOX/BX and qty > threshold
    //  - ">$1000" => true only if orderTotal > threshold
    let contractFreeNow = false;

    if (freightLower.includes('free')) {
      const matchBx = freightValue.match(/>(\d+)\s*BX/i);
      if (matchBx) {
        const threshold = parseInt(matchBx[1], 10);
        const isBox = uomUpper === 'BOX' || uomUpper === 'BX';
        contractFreeNow =
          Number.isFinite(threshold) && isBox && qtyNum != null && qtyNum > threshold;
      } else {
        const matchDollar = freightValue.match(/>\s*\$?\s*(\d+)/);
        if (matchDollar) {
          const threshold = parseInt(matchDollar[1], 10);
          contractFreeNow = Number.isFinite(threshold) && orderTotal > threshold;
        } else {
          contractFreeNow = true;
        }
      }
    }

    // -----------------------------
    // 3) Apply autopilot frtPay (guarded)
    // -----------------------------
    if (manualFreightOverrideRef.current || frtPayUserTouchedRef.current) {
      return;
    }

    // Decide next freight pay without nested ternaries (eslint: no-nested-ternary)
    let nextPay = isTmsCompanyCode(company) ? 'Free' : 'PPA';
    if (hasAcct) {
      nextPay = 'FC';
    }
    if (contractFreeNow) {
      nextPay = 'Free';
    }

    setFrtPay((prev) => {
      if (String(prev ?? '').trim() === nextPay) {
        return prev;
      }
      return nextPay;
    });
  }, [
    company,
    contractRows,
    existingFreightAccounts,
    freightAccountsReady,
    shipId,
    frtAccount,
    frtAccountUserEditing,
    qty,
    uom,
    price,
    carrier,
    frtPay,
    directShipFreightDefaults,
  ]);

  // ===========================================================================
  // BLOCK OEC.10 — Handlers: Company, Customer, Address
  // ===========================================================================
  const handleCompanyChange = useCallback(
    (e) => {
      const newCompany = e.target.value;

      customerSelectRequestSeqRef.current += 1;
      addressDisplayZipRef.current = null;
      setCustomerName('');
      setCompany(newCompany);

      setZip('');
      setZipForSearch('');
      setCity('');
      setState('');

      setMasId('');
      setShipId('');

      setCustomers([]);
      setSelectedCustomer('');

      setAddresses([]);
      setSelectedAddressIndex(null);
      setLastSelectedAddress(null);

      setBillTo(null);
      setNotes([]);

      setReceivingLine('');
      setConfirmTo('Purchasing');
      setConfirmToOptions(['Purchasing']);
      setPhone('');
      setPoNumber('');

      setPartError(null);
      setPartNo('');
      setPartInput('');
      setQty('');
      setUom('');
      setPrice('');
      setPriceAutoSource('none');
      clearProductQuote();
      setContractCode('');
      setContractNo('');
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);
      setSpecOrderBo(false);
      setSpecOrderMo(false);
      setSpecOrderDs(false);
      setSpecOrderBl(false);
      setSpecOrderCustomerNote('');
      setTermsValue('');
      setPreferredTermsCode('');

      resetFreightAutopilotGuards();

      zeroInsuranceManualRef.current = false;
      setFrtAccount('');
      setCarrier('FDX');
      setFrtPay(getDefaultFreightPayForCompany(newCompany));
      setShipVia('');
      setShipViaInitialized(false);
      setExistingFreightAccounts([]);
      setFreightReloadNonce((n) => n + 1);
      setFreightSaveOpen(false);
      setFreightSaveScope('shipto');
      setFreightSaveError(null);
      setFreightSaveBusy(false);

      setLookupError(null);
      setSalesReps({ rep: [], mngr: [], tsr: [] });
      setHouseAccount(isTmsCompanyCode(newCompany));
      setOmitCommentCodes(isTmsCompanyCode(newCompany));
      setCustomCommentsText('');
    },
    [clearProductQuote, resetFreightAutopilotGuards]
  );

  // ===========================================================================
  // BLOCK OEC.2A.CONFIRMTO.LOAD.0 — Load ConfirmTo dropdown options (distinct)
  // Trigger:
  //  - Called explicitly from handleAddressClick (and optionally other flows)
  // Rules:
  //  - Always reset confirmTo to "Purchasing" on context change
  //  - Always include "Purchasing" first
  // Notes:
  //  - getConfirmToNamesLast365() returns an ARRAY of strings after API normalization
  // ===========================================================================
  const loadConfirmToOptions = useCallback(
    async ({ nextCompany, customerNo, shipToCode }) => {
      // Always snap back to default when ship-to context changes
      setConfirmTo('Purchasing');

      if (!nextCompany || !customerNo || !shipToCode) {
        setConfirmToOptions(['Purchasing']);
        return;
      }

      setConfirmToLoading(true);

      try {
        const names = await getConfirmToNamesLast365({
          company: nextCompany,
          customerNo,
          shipToCode,
          days: 365,
        });

        // names is expected to be string[] (already normalized in API helper)
        const cleaned = (Array.isArray(names) ? names : [])
          .map((x) => String(x || '').trim())
          .filter(Boolean);

        const uniq = Array.from(new Set(cleaned));

        const next = ['Purchasing', ...uniq.filter((v) => v.toLowerCase() !== 'purchasing')];

        setConfirmToOptions(next.length ? next : ['Purchasing']);
      } catch {
        setConfirmToOptions(['Purchasing']);
      } finally {
        setConfirmToLoading(false);
      }
    },
    [] // intentionally stable; uses imported API helper + setState
  );

  const handleAddressClick = useCallback(
    (index, overrideAddresses, options = {}) => {
      const list = overrideAddresses || addresses;
      const syncZip = options.syncZip !== false;
      if (index < 0 || index >= list.length) {
        return;
      }

      const addr = list[index];
      requireManualStreetSelectionRef.current = false;
      setSelectedAddressIndex(index);

      const directShipContextKey = buildDirectShipContextKey({
        company,
        customerNo: addr.customerNo || '',
        shipToCode: addr.shipToCode || '',
      });
      const shouldCollectPatientAddress = isTmsPatientShipTo(company, addr.shipToCode);
      const existingDirectShipPatient =
        shouldCollectPatientAddress && directShipPatient.contextKey === directShipContextKey
          ? directShipPatient
          : null;

      if (shouldCollectPatientAddress) {
        setDirectShipPatient(
          existingDirectShipPatient || createEmptyDirectShipPatient(directShipContextKey)
        );
        setDirectShipDialogOpen(true);
      } else {
        setDirectShipPatient(createEmptyDirectShipPatient());
        setDirectShipDialogOpen(false);
      }

      // Clear phone when switching addresses
      setPhone('');
      setConfirmTo('Purchasing');

      setPartError(null);
      setPartNo('');
      setPartInput('');
      setQty('');
      setUom('');
      setPrice('');
      setPriceAutoSource('none');
      clearProductQuote();

      setContractCode('');
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);
      setSpecOrderBo(false);
      setSpecOrderMo(false);
      setSpecOrderDs(false);
      setSpecOrderBl(false);
      setSpecOrderCustomerNote('');
      setTermsValue('');
      setPreferredTermsCode('');
      zeroInsuranceManualRef.current = false;

      // ---------------------------------------------------------------------------
      // RESET FREIGHT TO BASELINE DEFAULT FOR NEW SHIP-TO
      // Rules:
      //  - Start at FedEx Ground Collect
      //  - FOB / contract logic will override AFTER this
      // ---------------------------------------------------------------------------
      resetFreightAutopilotGuards();

      setFrtAccountUserEditing(false);
      setFrtAccount('');

      const freightDefaults = getDirectShipFreightDefaults(company, addr.shipToCode);
      setCarrier(freightDefaults?.carrier || 'FDX');
      setFrtPay(freightDefaults?.frtPay || 'FC');
      setShipVia(freightDefaults?.shipVia || '');
      setShipViaInitialized(Boolean(freightDefaults?.shipVia));
      setExistingFreightAccounts([]);
      setFreightReloadNonce((n) => n + 1);
      setFreightSaveOpen(false);
      setFreightSaveScope('shipto');
      setFreightSaveError(null);
      setFreightSaveBusy(false);

      setLastSelectedAddress({
        customerNo: addr.customerNo || '',
        shipToCode: addr.shipToCode || '',
      });

      setMasId(addr.customerNo || '');
      setShipId(addr.shipToCode || '');

      // -----------------------------------------------------------------------
      // BLOCK OEC.2A.CONFIRMTO.ADDRESSCLICK.0 — Load ConfirmTo list for this ship-to
      // -----------------------------------------------------------------------
      loadConfirmToOptions({
        nextCompany: company,
        customerNo: addr.customerNo || '',
        shipToCode: addr.shipToCode || '',
      });

      // ---------------------------------------------------------------------------
      // LEGACY REQUIRED FIELDS
      // ---------------------------------------------------------------------------
      const nextCustomerName =
        existingDirectShipPatient?.shipToName ||
        options.customerName ||
        selectedCustomer ||
        addr.customerName ||
        addr.customername ||
        addr.billToName ||
        addr.customer ||
        '';

      setCustomerName(String(nextCustomerName ?? '').trim());

      setShipAddress1(existingDirectShipPatient?.address1 || addr.shipToAddress1 || '');
      setShipAddress2(existingDirectShipPatient?.address2 || addr.shipToAddress2 || '');

      // LOCATION
      setCity(existingDirectShipPatient?.city || addr.city || '');
      setState(existingDirectShipPatient?.state || addr.state || '');

      const selectedPostal = existingDirectShipPatient?.zip || addr.zipCode || '';
      if (syncZip && selectedPostal) {
        const postal = existingDirectShipPatient?.zip || normalizePostal5(selectedPostal);
        addressDisplayZipRef.current = normalizePostal5(postal);
        setZip(postal);
      }

      setBillTo({
        name: addr.billToName || '',
        address1: addr.billToAddress1 || '',
        address2: addr.billToAddress2 || '',
        city: addr.billToCity || '',
        state: addr.billToState || '',
        zip: addr.billToZipCode || '',
      });

      const baseAddress = existingDirectShipPatient?.address1 || addr.shipToAddress1 || '';
      const withPo =
        poNumber && poNumber.trim() ? `${baseAddress} PO#${poNumber.trim()}` : baseAddress;

      setReceivingLine(withPo);
    },
    [
      addresses,
      clearProductQuote,
      poNumber,
      selectedCustomer,
      company,
      directShipPatient,
      loadConfirmToOptions,
      resetFreightAutopilotGuards,
    ]
  );

  const handleCustomerSelect = useCallback(
    async (customer, options = {}) => {
      const requestSeq = customerSelectRequestSeqRef.current + 1;
      customerSelectRequestSeqRef.current = requestSeq;

      const shipToName = customer?.shipToName || '';
      const custNo = customer?.customerNo || '';
      const selectedZip = normalizePostal5(options?.zip || customer?.shipToZip || '');
      const preserveZipUntilStreetSelection =
        options?.preserveZipUntilStreetSelection === true ||
        requireManualStreetSelectionRef.current;
      const zipFromCustomerSelection = preserveZipUntilStreetSelection ? '' : selectedZip;

      setSelectedCustomer(shipToName);
      setAddresses([]);
      setSelectedAddressIndex(null);

      // Clear phone when selecting a new customer (ship-to will change)
      setPhone('');
      setConfirmTo('Purchasing');

      setPartError(null);
      setPartNo('');
      setPartInput('');
      setQty('');
      setUom('');
      setPrice('');
      setPriceAutoSource('none');
      clearProductQuote();

      setContractCode('');
      setContractNo('');
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);
      setSpecOrderBo(false);
      setSpecOrderMo(false);
      setSpecOrderDs(false);
      setSpecOrderBl(false);
      setSpecOrderCustomerNote('');
      setTermsValue('');
      setPreferredTermsCode('');

      resetFreightAutopilotGuards();
      zeroInsuranceManualRef.current = false;

      setFrtAccountUserEditing(false);
      setFrtAccount('');

      setMasId('');
      setShipId('');
      setCity('');
      setState('');
      setBillTo(null);
      setNotes([]);
      setCustomerName('');
      setShipAddress1('');
      setShipAddress2('');
      setDirectShipPatient(createEmptyDirectShipPatient());
      setDirectShipDialogOpen(false);
      setExistingFreightAccounts([]);
      setFreightReloadNonce((n) => n + 1);
      setFreightSaveOpen(false);
      setFreightSaveScope('shipto');
      setFreightSaveError(null);
      setFreightSaveBusy(false);

      const searchZip = zipFromCustomerSelection || zipForSearch || zip;
      if (!custNo || !searchZip || searchZip.length < 3) {
        return;
      }

      if (zipFromCustomerSelection) {
        setZip(zipFromCustomerSelection);
        setZipForSearch(zipFromCustomerSelection);
      }

      try {
        setLookupLoading(true);

        let rows = await getAddressesByCustomer({ company, zip: searchZip, customerNo: custNo });

        if (customerSelectRequestSeqRef.current !== requestSeq) {
          return;
        }

        rows = rows.filter(
          (addr, index, self) =>
            index ===
            self.findIndex(
              (a) =>
                a.customerNo === addr.customerNo &&
                a.shipToCode === addr.shipToCode &&
                a.shipToAddress1 === addr.shipToAddress1 &&
                a.shipToAddress2 === addr.shipToAddress2
            )
        );

        setAddresses(rows);

        if (lastSelectedAddress) {
          const idx = rows.findIndex(
            (a) =>
              a.customerNo === lastSelectedAddress.customerNo &&
              a.shipToCode === lastSelectedAddress.shipToCode
          );
          if (idx >= 0) {
            handleAddressClick(idx, rows, { syncZip: false, customerName: shipToName });
            return;
          }
        }

        if (rows.length === 1 && !requireManualStreetSelectionRef.current) {
          handleAddressClick(0, rows, { syncZip: false, customerName: shipToName });
        }
      } catch (err) {
        if (customerSelectRequestSeqRef.current !== requestSeq) {
          return;
        }

        const status = err?.response?.status;
        setLookupError(status ? `Address lookup failed (${status})` : 'Address lookup failed.');
      } finally {
        if (customerSelectRequestSeqRef.current === requestSeq) {
          setLookupLoading(false);
        }
      }
    },
    [
      clearProductQuote,
      company,
      zipForSearch,
      zip,
      lastSelectedAddress,
      handleAddressClick,
      resetFreightAutopilotGuards,
    ]
  );

  // ===========================================================================
  // BLOCK OEC.PO_ALERT.DEBOUNCE.0 — Debounced PO warning alert helper
  // Prevents spamming window.alert when holding down Enter or on rapid triggers
  // ===========================================================================
  const lastPoAlertRef = useRef({});
  const PO_ALERT_DEBOUNCE_MS = 1000;

  const showAlertDebounced = useCallback((message, alertKey = 'default') => {
    const now = Date.now();
    const lastTime = lastPoAlertRef.current[alertKey] || 0;

    if (now - lastTime < PO_ALERT_DEBOUNCE_MS) {
      return false;
    }

    lastPoAlertRef.current[alertKey] = now;
    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert(message);
    }
    return true;
  }, []);

  // ===========================================================================
  // BLOCK OEC.10P — TMS Direct Ship to Patient handlers
  // ===========================================================================
  const openDirectShipPatientDialog = useCallback(() => {
    if (isTmsPatientShipTo(company, shipId)) {
      setDirectShipDialogOpen(true);
    }
  }, [company, shipId]);

  const closeDirectShipPatientDialog = useCallback(() => {
    setDirectShipDialogOpen(false);
  }, []);

  const submitDirectShipPatient = useCallback(
    async (value) => {
      const contextKey = buildDirectShipContextKey({
        company,
        customerNo: masId,
        shipToCode: shipId,
      });

      const normalized = {
        contextKey,
        shipToName: String(value?.shipToName ?? '').trim(),
        address1: 'OR Residence',
        rawAddress: String(value?.rawAddress ?? '').trim(),
        address2: String(value?.address2 ?? '').trim(),
        city: String(value?.city ?? '').trim(),
        state: String(value?.state ?? '')
          .trim()
          .toUpperCase()
          .slice(0, 2),
        zip: String(value?.zip ?? '').trim(),
        studyId: String(value?.studyId ?? '').trim(),
        studyAccessoryKit: String(value?.studyAccessoryKit ?? '').trim(),
        patientPhoneNumber: String(value?.patientPhoneNumber ?? '').trim(),
      };

      if (!isTmsPatientShipTo(company, shipId) || !isCompleteDirectShipPatient(normalized)) {
        return;
      }

      setDirectShipPatient(normalized);
      setCustomerName(normalized.shipToName);
      setShipAddress1(normalized.address1);
      setShipAddress2(normalized.address2);
      setCity(normalized.city);
      setState(normalized.state);
      setZip(normalized.zip);

      if (normalized.studyAccessoryKit) {
        setPartNo(normalized.studyAccessoryKit);
        setPartInput(normalized.studyAccessoryKit);
      }

      if (normalized.patientPhoneNumber) {
        setPhone(normalized.patientPhoneNumber);
      }

      const autoPo = normalized.studyId ? `TMS${normalized.studyId}` : '';
      if (autoPo) {
        setPoNumber(autoPo);

        try {
          const matches = await checkPoDuplicate({
            company,
            customerNo: masId,
            poNumber: autoPo,
          });

          if (matches.length > 0) {
            const lines = matches
              .map((m) => {
                const label = m.source === 'SO' ? 'Order Date' : 'Invoice Date';
                return `PO: ${m.customerPONo} - ${label}: ${m.date}`;
              })
              .join('\n');

            showAlertDebounced(
              `Duplicate PO found in System or Invoice History:\n${lines}\n**VERIFY THAT IT IS NOT A DUPLICATE!!**`,
              `dup_po_${autoPo}`
            );
          }
        } catch {
          // preserve existing UX: a failed warning check should not block dialog submit
        }
      }

      const withPo =
        autoPo || (poNumber && poNumber.trim())
          ? `${normalized.address1} PO#${autoPo || poNumber.trim()}`
          : normalized.address1;
      setReceivingLine(withPo);
      setDirectShipDialogOpen(false);
    },
    [company, masId, poNumber, shipId, showAlertDebounced]
  );

  // ==========================================================================
  // BLOCK OEC.10P2 — Load pending/recording studies for TMS direct-ship dialog
  // ==========================================================================
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!directShipDialogOpen || !directShipPatientActive) {
        setDirectShipStudies([]);
        setDirectShipStudiesError('');
        setDirectShipStudiesLoading(false);
        return;
      }

      if (!selectedAddressFacilityZip) {
        setDirectShipStudies([]);
        setDirectShipStudiesError('No facility ZIP is available for study lookup.');
        setDirectShipStudiesLoading(false);
        return;
      }

      try {
        setDirectShipStudiesLoading(true);
        setDirectShipStudiesError('');

        const rows = await getBitrhythmStudies({
          company: 'tms',
          facilityZipCode: selectedAddressFacilityZip,
        });

        if (!cancelled) {
          setDirectShipStudies(toArray(rows));
        }
      } catch {
        if (!cancelled) {
          setDirectShipStudies([]);
          setDirectShipStudiesError('Study lookup failed. You can still enter address manually.');
        }
      } finally {
        if (!cancelled) {
          setDirectShipStudiesLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [directShipDialogOpen, directShipPatientActive, selectedAddressFacilityZip]);

  // ===========================================================================
  // BLOCK OEC.10P1 — Manual TMS ShipToCode override from the header field
  // ===========================================================================
  const handleShipIdChange = useCallback(
    (value) => {
      if (
        String(company ?? '')
          .trim()
          .toLowerCase() !== 'tms' ||
        !masId
      ) {
        return;
      }

      const nextShipId = String(value ?? '')
        .trim()
        .toUpperCase();
      const baseShipId = String(selectedAddressShipId ?? '')
        .trim()
        .toUpperCase();
      const currentShipId = String(shipId ?? '')
        .trim()
        .toUpperCase();

      if (nextShipId !== 'P' && nextShipId !== baseShipId) {
        return;
      }

      if (nextShipId === currentShipId) {
        if (nextShipId === 'P') {
          setDirectShipDialogOpen(true);
        }
        return;
      }

      setPhone('');
      setConfirmTo('Purchasing');
      setPartError(null);
      setPartNo('');
      setPartInput('');
      setQty('');
      setUom('');
      setPrice('');
      setPriceAutoSource('none');
      clearProductQuote();
      setContractCode('');
      setContractNo('');
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);
      setSpecOrderBo(false);
      setSpecOrderMo(false);
      setSpecOrderDs(false);
      setSpecOrderBl(false);
      setSpecOrderCustomerNote('');
      setTermsValue('');
      setPreferredTermsCode('');
      setNotes([]);
      setSalesReps({ rep: [], mngr: [], tsr: [] });
      setHouseAccount(isTmsCompanyCode(company));
      setCreditHold(false);

      resetFreightAutopilotGuards();
      zeroInsuranceManualRef.current = false;
      setFrtAccountUserEditing(false);
      setFrtAccount('');
      const freightDefaults = getDirectShipFreightDefaults(company, nextShipId);
      setCarrier(freightDefaults?.carrier || 'FDX');
      setFrtPay(freightDefaults?.frtPay || getDefaultFreightPayForCompany(company));
      setShipVia(freightDefaults?.shipVia || '');
      setShipViaInitialized(Boolean(freightDefaults?.shipVia));
      setExistingFreightAccounts([]);
      setFreightReloadNonce((n) => n + 1);
      setFreightSaveOpen(false);
      setFreightSaveScope('shipto');
      setFreightSaveError(null);
      setFreightSaveBusy(false);

      setShipId(nextShipId);
      loadConfirmToOptions({
        nextCompany: company,
        customerNo: masId,
        shipToCode: nextShipId,
      });

      if (nextShipId === 'P') {
        const contextKey = buildDirectShipContextKey({
          company,
          customerNo: masId,
          shipToCode: 'P',
        });
        const existingPatient =
          directShipPatient.contextKey === contextKey
            ? directShipPatient
            : createEmptyDirectShipPatient(contextKey);

        setDirectShipPatient(existingPatient);
        setDirectShipDialogOpen(true);
        return;
      }

      setDirectShipPatient(createEmptyDirectShipPatient());
      setDirectShipDialogOpen(false);

      const addr =
        selectedAddressIndex === null || selectedAddressIndex === undefined
          ? null
          : addresses?.[selectedAddressIndex] || null;

      if (!addr) {
        return;
      }

      setCustomerName(
        addr.customerName ||
          addr.customername ||
          addr.billToName ||
          addr.customer ||
          selectedCustomer ||
          ''
      );
      setShipAddress1(addr.shipToAddress1 || '');
      setShipAddress2(addr.shipToAddress2 || '');
      setCity(addr.city || '');
      setState(addr.state || '');

      const postal = normalizePostal5(addr.zipCode || '');
      if (postal) {
        setZip(postal);
      }

      const baseAddress = addr.shipToAddress1 || '';
      const withPo =
        poNumber && poNumber.trim() ? `${baseAddress} PO#${poNumber.trim()}` : baseAddress;
      setReceivingLine(withPo);
    },
    [
      addresses,
      clearProductQuote,
      company,
      directShipPatient,
      loadConfirmToOptions,
      masId,
      poNumber,
      resetFreightAutopilotGuards,
      selectedAddressIndex,
      selectedAddressShipId,
      selectedCustomer,
      shipId,
    ]
  );

  const handleShipToSearchSelect = useCallback((shipToRow) => {
    customerSelectRequestSeqRef.current += 1;

    const shipToName = String(shipToRow?.shipToName || '').trim();
    const selectedZip = toPostal5(shipToRow?.shipToZip || '');

    if (!shipToName) {
      return;
    }

    if (!selectedZip) {
      return;
    }

    // Reset current list selection so the normal ZIP -> customer -> address flow repopulates cleanly.
    requireManualStreetSelectionRef.current = true;
    setSelectedCustomer('');
    setCustomers([]);
    setAddresses([]);
    setSelectedAddressIndex(null);
    setMasId('');
    setShipId('');
    setCustomerName('');
    setShipAddress1('');
    setShipAddress2('');
    setCity('');
    setState('');
    setBillTo(null);
    setNotes([]);
    setLastSelectedAddress(null);

    setShipToSearchError('');
    shouldFocusCustomerListRef.current = true;
    setZip(selectedZip);
    setZipForSearch(selectedZip);
  }, []);

  const clearShipToSearch = useCallback(() => {
    shipToSearchRequestSeqRef.current += 1;
    setShipToSearchRows([]);
    setShipToSearchLoading(false);
    setShipToSearchError('');
  }, []);

  const searchShipToAddresses = useCallback(
    async (query) => {
      const normalizedQuery = normalizeShipToSearchTerm(query);

      if (!isShipToSearchLongEnough(normalizedQuery)) {
        clearShipToSearch();
        return;
      }

      const requestSeq = shipToSearchRequestSeqRef.current + 1;
      shipToSearchRequestSeqRef.current = requestSeq;
      setShipToSearchError('');
      setShipToSearchLoading(true);

      try {
        const rows = await getShipToAddresses({ company, search: normalizedQuery });
        if (shipToSearchRequestSeqRef.current !== requestSeq) {
          return;
        }

        const normalized = normalizeShipToSearchRows(rows);
        setShipToSearchRows(dedupeShipToSearchRows(normalized));
      } catch (err) {
        if (shipToSearchRequestSeqRef.current !== requestSeq) {
          return;
        }

        const status = err?.response?.status;
        setShipToSearchError(
          status ? `Ship-to lookup failed (${status})` : 'Ship-to lookup failed.'
        );
        setShipToSearchRows([]);
      } finally {
        if (shipToSearchRequestSeqRef.current === requestSeq) {
          setShipToSearchLoading(false);
        }
      }
    },
    [clearShipToSearch, company]
  );

  // BLOCK OEC.10A — Refresh for every selection and every dialog opening.
  // Readiness is keyed to the exact request, so stale rows never label a new context.
  useEffect(() => {
    const seq = ++freightRequestSeqRef.current;
    let cancelled = false;
    setFreightLoadError('');
    setFreightShipTos([]);
    if (!masId || !carrier) {
      setExistingFreightAccounts([]);
      setFreightDataKey('');
      return undefined;
    }
    const run = async () => {
      try {
        const result = await listSavedFreightAccounts({
          company,
          customerNo: masId,
          carrier,
          includeShipTos: freightSaveOpen,
        });
        if (!Array.isArray(result?.rows) || (freightSaveOpen && !Array.isArray(result?.shipTos))) {
          throw new Error('Invalid freight account response');
        }
        if (cancelled || seq !== freightRequestSeqRef.current) {
          return;
        }
        setExistingFreightAccounts(result.rows);
        setFreightShipTos(result.shipTos || []);
        setFreightDataKey(freightQueryKey);
      } catch {
        if (!cancelled && seq === freightRequestSeqRef.current) {
          setExistingFreightAccounts([]);
          setFreightLoadError('Unable to load freight accounts and ship-tos. Retry before saving.');
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [company, masId, shipId, carrier, freightSaveOpen, freightQueryKey]);

  // ============================================================================
  // BLOCK OEC.10B — Freight UI handlers
  // ============================================================================

  const handleFrtPayChange = useCallback((next) => {
    // User explicitly chose a pay type => do not override on this part unless reset.
    manualFreightOverrideRef.current = true;
    frtPayUserTouchedRef.current = true;
    setFrtPay(next);
  }, []);

  const handleCarrierChange = useCallback((next) => {
    // Carrier changes are always user intent; preserve.
    manualFreightOverrideRef.current = true;
    frtPayUserTouchedRef.current = true;

    // Re-apply carrier defaults on next shipvia load/validation pass.
    setShipVia('');
    setShipViaInitialized(false);

    setFrtAccount('');
    setFrtAccountUserEditing(false);
    frtAccountUserTouchedRef.current = false;
    setCarrier(next);
  }, []);

  // Called on input focus / typing (PartPanel wires this to onFocus/onChange edit start)
  const handleFrtAccountUserEdit = useCallback((editing) => {
    // If user is editing the freight account field, mark touched so autopilot won't replace it.
    if (editing) {
      // Capture value at the start of this edit session.
      if (!frtAccountEditingRef.current) {
        frtAccountEditStartValueRef.current = String(frtAccountRef.current ?? '').trim();
      }
      frtAccountUserTouchedRef.current = true;
      manualFreightOverrideRef.current = true; // treat as explicit user override
    }
    setFrtAccountUserEditing(Boolean(editing));
  }, []);

  const normalizeCarrierCode = useCallback((v) => {
    const c = String(v ?? '')
      .trim()
      .toUpperCase();
    return c === 'FEDEX' ? 'FDX' : c;
  }, []);

  const validateFreightAccount = useCallback(
    (value, carrierCode) => {
      const raw = String(value ?? '').trim();
      const carrierNorm = normalizeCarrierCode(carrierCode);

      if (!raw) {
        return { ok: true, value: '' };
      }

      if (carrierNorm === 'FDX') {
        const digitsOnly = raw.replace(/\s+/g, '');
        const isValid = /^\d{9}$/.test(digitsOnly);
        if (!isValid) {
          window.alert('FedEx numbers are 9 digits.');
          return { ok: false };
        }
        return { ok: true, value: digitsOnly };
      }

      if (carrierNorm === 'UPS') {
        const sanitized = raw.replace(/[\s-]/g, '').toUpperCase();
        const isValid = /^[A-Z0-9]{6}$/.test(sanitized);
        if (!isValid) {
          window.alert(
            'UPS numbers are 6 characters (letters/digits). A single space after the third character is allowed.'
          );
          return { ok: false };
        }
        return { ok: true, value: sanitized };
      }

      return { ok: true, value: raw };
    },
    [normalizeCarrierCode]
  );

  const normalizeShipToCode = useCallback((v) => String(v ?? '').trim(), []);

  const maybePromptFreightSave = useCallback(
    (value) => {
      const acct = String(value ?? '').trim();

      if (!acct || frtPay !== 'FC' || !masId || !carrier) {
        return;
      }

      const shipCode = normalizeShipToCode(shipId).toUpperCase();
      const exact = existingFreightAccounts.find(
        (r) =>
          String(r.shipToCode ?? '')
            .trim()
            .toUpperCase() === shipCode
      );
      // A matching default must not suppress updating a different explicit ship-to.
      // Allow missing exact records to be saved, even when their default matches.
      if (freightAccountsReady && exact && String(exact.accountNo ?? '').trim() === acct) {
        return;
      }
      setFreightSaveContext({
        key: freightContextKey,
        company,
        customerNo: masId,
        shipToCode: shipId,
        carrier,
        accountNo: acct,
      });
      setFreightSaveScope(shipId ? 'shipto' : 'all');
      setFreightSaveError(null);
      setFreightSaveOpen(true);
    },
    [
      carrier,
      company,
      existingFreightAccounts,
      freightAccountsReady,
      freightContextKey,
      frtPay,
      masId,
      shipId,
      normalizeShipToCode,
    ]
  );

  // BLOCK OEC.10C — Sage locations + saved rows + current location.
  const freightSavePreviewRows = useMemo(() => {
    if (!freightAccountsReady || freightSaveContext?.key !== freightContextKey) {
      return [];
    }
    return buildFreightAccountPreview({
      customerNo: masId,
      carrier,
      shipToCode: shipId,
      scope: freightSaveScope,
      savedRows: existingFreightAccounts,
      shipTos: freightShipTos,
    });
  }, [
    freightAccountsReady,
    freightSaveContext,
    freightContextKey,
    masId,
    carrier,
    shipId,
    freightSaveScope,
    existingFreightAccounts,
    freightShipTos,
  ]);

  // BLOCK OEC.4B.1 — Maybe prompt removal when user clears Freight Acct
  // --------------------------------------------------------------------------
  // Trigger condition:
  //  - user was manually editing AND blurred with empty value
  //  - AND there is at least one saved non-empty account for this
  //    (customerNo, carrier) context across any stored ship-to row
  // =========================================================================
  const maybePromptFreightRemove = useCallback(
    (force = false) => {
      if (frtPay !== 'FC' || !masId || !carrier) {
        return;
      }

      const shipCode = normalizeShipToCode(shipId);
      const shipRow = existingFreightAccounts.find(
        (r) => normalizeShipToCode(r?.shipToCode) === shipCode
      );
      const customerRow = existingFreightAccounts.find(
        (r) => normalizeShipToCode(r?.shipToCode) === ''
      );

      const shipAcct = String(shipRow?.accountNo ?? '').trim();
      const customerAcct = String(customerRow?.accountNo ?? '').trim();
      const hasAnySavedAcct = existingFreightAccounts.some(
        (r) => String(r?.accountNo ?? '').trim() !== ''
      );

      if (!hasAnySavedAcct && !force) {
        return;
      }

      // Default scope:
      // - If we have a ship-to code, default to "shipto" so the user can create
      //   a deliberate blank override (prevents customer-wide fallback).
      // - Otherwise default to clearing all.
      const shouldDefaultToShipTo = Boolean(shipId) && Boolean(shipAcct || customerAcct);
      setFreightRemoveScope(shouldDefaultToShipTo ? 'shipto' : 'all');
      setFreightRemoveError(null);
      setFreightRemoveOpen(true);
    },
    [carrier, existingFreightAccounts, frtPay, masId, shipId, normalizeShipToCode]
  );

  // =========================================================================
  // BLOCK OEC.4B.2 — Remove dialog handlers
  // =========================================================================
  const closeFreightRemoveDialog = useCallback(() => {
    if (freightRemoveBusy) {
      return;
    }
    setFreightRemoveOpen(false);
    setFreightRemoveError(null);
  }, [freightRemoveBusy]);

  const submitFreightRemove = useCallback(async () => {
    if (!masId || !carrier) {
      setFreightRemoveOpen(false);
      return;
    }

    setFreightRemoveBusy(true);
    setFreightRemoveError(null);

    try {
      const scope = freightRemoveScope === 'all' ? 'all' : 'shipto';
      const shipToCode = scope === 'shipto' ? shipId || '' : '';

      if (scope === 'shipto' && !shipToCode) {
        setFreightRemoveError('No ship-to selected (cannot clear ship-to scope).');
        return;
      }

      await clearFreightAccounts({ customerNo: masId, carrier, scope, shipToCode });

      setFreightReloadNonce((n) => n + 1);

      // UI is already cleared (user blanked the field); just close + reset edit guard.
      setFreightRemoveOpen(false);
      setFrtAccountUserEditing(false);
    } catch {
      setFreightRemoveError('Failed to clear freight account(s). Please retry.');
    } finally {
      setFreightRemoveBusy(false);
    }
  }, [carrier, freightRemoveScope, masId, shipId]);

  const handleFrtAccountBlur = useCallback(
    (valueFromInput) => {
      const acct = String(valueFromInput ?? frtAccount ?? '').trim();
      const carrierNorm = normalizeCarrierCode(carrier);
      const validation = validateFreightAccount(acct, carrierNorm);

      if (!validation.ok) {
        setFrtAccount('');
        return;
      }

      setFrtAccount(validation.value);

      // When the user clears the field, prompt for removal choices.
      if (frtAccountUserEditing && validation.value === '') {
        const clearedFromNonEmpty = String(frtAccountEditStartValueRef.current ?? '').trim() !== '';
        maybePromptFreightRemove(clearedFromNonEmpty);
        frtAccountEditStartValueRef.current = '';
        return;
      }

      if (frtAccountUserEditing) {
        maybePromptFreightSave(validation.value);
      }

      frtAccountEditStartValueRef.current = '';
    },
    [
      carrier,
      frtAccount,
      frtAccountUserEditing,
      maybePromptFreightRemove,
      maybePromptFreightSave,
      normalizeCarrierCode,
      validateFreightAccount,
    ]
  );

  const closeFreightSaveDialog = useCallback(() => {
    if (freightSaveBusy || freightMutationBusyRef.current) {
      return;
    }
    setFreightSaveOpen(false);
    setFreightSaveError(null);
  }, [freightSaveBusy]);
  // ==========================================================================
  // BLOCK OEC.FRT.SAVE — Save the exact context shown in the dialog.
  const submitFreightSave = useCallback(async () => {
    const context = freightSaveContext;
    if (
      freightMutationBusyRef.current ||
      !context ||
      context.key !== freightLiveContextRef.current
    ) {
      return;
    }
    if (!freightAccountsReady || freightLoadError) {
      setFreightSaveError('Wait for the accounts to load successfully before saving.');
      return;
    }
    if (
      !['shipto', 'all'].includes(freightSaveScope) ||
      (freightSaveScope === 'shipto' && !context.shipToCode)
    ) {
      setFreightSaveError('Select a valid save scope and ship-to.');
      return;
    }
    const validation = validateFreightAccount(context.accountNo, context.carrier);
    if (!validation.ok || !validation.value) {
      setFreightSaveError('Enter a valid freight account before saving.');
      return;
    }
    freightMutationBusyRef.current = true;
    setFreightSaveBusy(true);
    setFreightSaveError(null);
    // Discard any older list request that could otherwise overwrite refreshed values.
    freightRequestSeqRef.current += 1;
    try {
      const payload = {
        company: context.company,
        customerNo: context.customerNo,
        shipToCode: context.shipToCode,
        carrier: context.carrier,
        accountNo: validation.value,
      };
      const result =
        freightSaveScope === 'all'
          ? await bulkSetFreightAccounts(payload)
          : await saveFreightAccount(payload);
      if (result?.ok !== true) {
        throw new Error('Save was not acknowledged');
      }
      if (freightLiveContextRef.current === context.key) {
        setFrtAccount(validation.value);
        setFreightSaveOpen(false);
        setFreightSaveContext(null);
        setFrtAccountUserEditing(false);
        setFreightReloadNonce((n) => n + 1);
      }
    } catch {
      if (freightLiveContextRef.current === context.key) {
        setFreightSaveError('Failed to save freight account. Please retry.');
      }
    } finally {
      freightMutationBusyRef.current = false;
      setFreightSaveBusy(false);
    }
  }, [
    freightSaveContext,
    freightAccountsReady,
    freightLoadError,
    freightSaveScope,
    validateFreightAccount,
  ]);

  // ======================================================
  // BLOCK OEC.11 — Customer Notes (ShipTo)
  // ----------------------------------------------------------------------------
  // Source of truth for "house flags" that come from the notes/flags endpoint:
  // - creditHold
  // - houseAccount
  //
  // Key rule:
  // - These flags are customer-contextual. When (company/masId/shipId) changes,
  //   we must CLEAR prior values first, then set based on the latest response.
  //
  // Why this matters:
  // - Without clearing, a previous customer’s "true" state can leak into the next
  //   selection and the UI (checkbox/dialog) becomes inconsistent.
  // ======================================================
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const ignoreHouseAccount = Number(houseAccountReps?.split ?? 0) === 2;

      // Don’t call API with blank shipToCode (server returns 400)
      // Also: clear contextual flags so we don’t leak prior customer state.
      if (!company || !masId || !shipId) {
        setNotes([]);
        setHouseAccount(isTmsCompanyCode(company));
        setCreditHold(false);
        return;
      }

      // Step 1 — Clear state for *this* customer context before applying results.
      // This guarantees we only reflect the newest lookup.
      setHouseAccount(isTmsCompanyCode(company));
      setCreditHold(false);

      try {
        const res = await getCustomerNotes({
          company,
          customerNo: masId,
          shipToCode: shipId,
        });

        if (cancelled) {
          return;
        }

        // Step 2 — Normalize notes array (API may return either:
        //   - array of strings
        //   - { notes: string[], flags: {...} }
        let nextNotes = [];
        if (Array.isArray(res)) {
          nextNotes = res;
        } else if (Array.isArray(res?.notes)) {
          nextNotes = res.notes;
        }
        setNotes(nextNotes);

        // Step 3 — Prefer explicit flags from API (most reliable).
        const flags = res?.flags || {};
        const apiHouseAccount = Boolean(flags.houseAccount);
        const apiCreditHold = Boolean(flags.creditHold);

        if (apiHouseAccount && !houseAccountIgnoreRef.current && !ignoreHouseAccount) {
          setHouseAccount(true);
        }

        if (apiCreditHold) {
          setCreditHold(true);
        }

        // Step 4 — Fallback detection from notes text (legacy robustness).
        // Only run fallback checks if the API did not explicitly flag it.
        if (!apiHouseAccount || !apiCreditHold) {
          const joined = (nextNotes || []).join(' ');
          const joinedUp = String(joined || '').toUpperCase();

          if (
            !apiHouseAccount &&
            joinedUp.includes('HOUSE ACCOUNT') &&
            !houseAccountIgnoreRef.current &&
            !ignoreHouseAccount
          ) {
            setHouseAccount(true);
          }

          if (!apiCreditHold) {
            const creditHoldRegex = /CREDIT\s*HOLD/i;
            const creditMatch =
              creditHoldRegex.test(joined) ||
              (joinedUp.includes('CREDIT') && joinedUp.includes('HOLD'));

            if (creditMatch) {
              setCreditHold(true);
            }
          }
        }
      } catch (e) {
        if (cancelled) {
          return;
        }
        // On error, clear notes and contextual flags (safe default).
        setNotes([]);
        setHouseAccount(isTmsCompanyCode(company));
        setCreditHold(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [company, masId, shipId, houseAccountReps?.split]);

  // If notes are updated by any path (API or other), ensure we pick up a Credit Hold marker
  useEffect(() => {
    try {
      if (!Array.isArray(notes) || notes.length === 0) {
        return;
      }

      // If already set, keep it (we don't auto-clear here)
      if (creditHold) {
        return;
      }

      const joined = notes.join(' ');
      const joinedUp = String(joined || '').toUpperCase();
      const creditHoldRegex = /CREDIT\s*HOLD/i;
      const creditMatch =
        creditHoldRegex.test(joined) || (joinedUp.includes('CREDIT') && joinedUp.includes('HOLD'));

      if (creditMatch) {
        setCreditHold(true);
      }
    } catch (e) {
      // ignore
    }
  }, [notes, creditHold]);

  // ===========================================================================
  // BLOCK OEC.11B — EFFECT: Sales reps lookup
  // ===========================================================================
  useEffect(() => {
    let cancelled = false;

    if (!masId || !shipId) {
      setSalesReps({ rep: [], mngr: [], tsr: [] });
      setRepSlots([]);
      return () => {
        cancelled = true;
      };
    }

    const st = String(state ?? '')
      .trim()
      .toUpperCase();
    let salesRepZip = zip;
    if (directShipPatientActive && directShipPatientComplete) {
      salesRepZip = directShipPatient.zip;
    } else if (selectedAddressIndex != null && addresses?.[selectedAddressIndex]?.zipCode) {
      salesRepZip = addresses[selectedAddressIndex].zipCode;
    }
    const z5 = normalizeUsZip5(salesRepZip);

    if (!st || st.length < 2 || !z5 || z5.length < 3) {
      setSalesReps({ rep: [], mngr: [], tsr: [] });
      setRepSlots([]);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      try {
        const payload = await getSalesReps({ company, state: st, zip: z5 });

        const rep = toArray(payload?.rep);
        const mngr = toArray(payload?.mngr);
        const tsr = toArray(payload?.tsr);

        if (!cancelled) {
          setSalesReps({ rep, mngr, tsr });

          // ✅ submit source-of-truth: include all reps shown in SalesRepsPanel
          // Order: reps (primary) → managers → tsr; de-duplicate by rep code
          const merged = [...rep, ...mngr, ...tsr];
          const seen = new Set();
          const mergedUnique = merged.filter((entry) => {
            const repCode = String(entry?.rep ?? entry?.no ?? entry?.repId ?? entry?.rep_id ?? '')
              .trim()
              .toUpperCase();
            if (!repCode) {
              return false;
            }
            if (seen.has(repCode)) {
              return false;
            }
            seen.add(repCode);
            return true;
          });

          const slots = normalizeRepSlots(mergedUnique);
          setRepSlots(slots);
        }
      } catch (err) {
        if (!cancelled) {
          setSalesReps({ rep: [], mngr: [], tsr: [] });
          setRepSlots([]);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    masId,
    shipId,
    state,
    zip,
    addresses,
    selectedAddressIndex,
    company,
    normalizeRepSlots,
    directShipPatient.zip,
    directShipPatientActive,
    directShipPatientComplete,
  ]);

  // ---------------------------------------------------------------------------
  // BLOCK OEC.11B2.HOUSE.REPS.EFFECT.0 — Load house account reps (Rep1/Rep2)
  // Rules:
  //  - Exact (masId, shipId) wins
  //  - Fallback (masId, shiptocode=='')
  //  - House mode is active ONLY when rep1 exists (per requirement)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!company || !masId) {
        // Clear house meta + ensure checkboxes are not stuck on from prior customer
        setHouseAccountReps({
          rep1: '',
          rep2: '',
          rep1Name: '',
          rep2Name: '',
          split: 0,
          revReqChk: 0,
        });

        houseAccountIgnoreRef.current = false;
        setRevReq(false);

        setHouseAccount(isTmsCompanyCode(company));
        setSplitCommission(false);
        return;
      }

      try {
        const res = await getHouseAccountReps({
          company,
          customerNo: masId,
          shipToCode: shipId || '',
        });

        if (cancelled) {
          return;
        }

        const mode = String(res?.mode ?? '')
          .trim()
          .toUpperCase();
        const splitRaw = res?.split ?? res?.Split;
        const split = Number(splitRaw);
        const hasSplit = splitRaw !== null && splitRaw !== undefined && splitRaw !== '';
        const splitSafe = Number.isFinite(split) ? split : null;

        const revReqRaw = res?.revReqChk ?? res?.rev_req_chk ?? 0;
        const revReqChk = Number(revReqRaw) === 1 ? 1 : 0;

        // ---------------------------------------------------------------------
        // BLOCK OEC.REVREQ.FORCE.2 — Force Revision Requested ON when flagged
        // Rule:
        //  - If revReqChk === 1, setRevReq(true) ONCE per customer context
        //  - Never auto-set it false when revReqChk === 0
        // ---------------------------------------------------------------------
        if (revReqChk === 1 && !revReqForcedAppliedRef.current) {
          revReqForcedAppliedRef.current = true;
          setRevReq(true);
        }

        // mode=NONE or missing split means customer is NOT in house_accounts table
        if (mode === 'NONE' || !hasSplit || splitSafe === null) {
          houseAccountIgnoreRef.current = false;
          setHouseAccount(isTmsCompanyCode(company));
          setSplitCommission(false);

          setHouseAccountReps({
            rep1: '',
            rep2: '',
            rep1Name: '',
            rep2Name: '',
            split: 0,
            revReqChk,
          });
          return;
        }

        // split=2 means IGNORE (treat as not in list)
        houseAccountIgnoreRef.current = splitSafe === 2;

        // Base reps (as returned)
        const rep1 = String(res?.rep1 ?? '').trim();
        const rep2 = String(res?.rep2 ?? '').trim();
        const rep1Name = String(res?.rep1Name ?? '').trim();
        const rep2Name = String(res?.rep2Name ?? '').trim();

        // ---------------------------------------------------------------------
        // OEC.HOUSE.SPLIT.MODE.0 — Enforce checkbox + override behavior
        // split = 0 => house account checkbox ON
        // split = 1 => split account checkbox ON
        // split = 2 => ignore reps and behave as if not in list
        // ---------------------------------------------------------------------
        if (splitSafe === 0) {
          houseAccountIgnoreRef.current = false;
          setHouseAccount(true);
          setSplitCommission(false);

          setHouseAccountReps({
            rep1,
            rep2,
            rep1Name,
            rep2Name,
            split: splitSafe,
            revReqChk,
          });
        } else if (splitSafe === 1) {
          houseAccountIgnoreRef.current = false;
          setHouseAccount(false);

          setSplitCommission(true);

          // Keep reps available for override display (if rep1 exists)
          setHouseAccountReps({
            rep1,
            rep2,
            rep1Name,
            rep2Name,
            split: splitSafe,
            revReqChk,
          });
        } else if (splitSafe === 2) {
          houseAccountIgnoreRef.current = true;
          // Treat like customer is NOT in house list:
          // - do NOT force house/split checkboxes
          // - do NOT allow house rep override to leak into submit/UI
          setHouseAccount(isTmsCompanyCode(company));
          setSplitCommission(false);

          setHouseAccountReps({
            rep1: '',
            rep2: '',
            rep1Name: '',
            rep2Name: '',
            split: splitSafe,
            revReqChk,
          });
        } else {
          // Unknown => safest default: no forced overrides
          setHouseAccount(isTmsCompanyCode(company));
          setSplitCommission(false);

          setHouseAccountReps({
            rep1: '',
            rep2: '',
            rep1Name: '',
            rep2Name: '',
            split: 0,
            revReqChk,
          });
        }
      } catch {
        if (cancelled) {
          return;
        }

        // Fail-safe: clear override + clear forced modes
        setHouseAccountReps({
          rep1: '',
          rep2: '',
          rep1Name: '',
          rep2Name: '',
          split: 0,
          revReqChk: 0,
        });

        setHouseAccount(isTmsCompanyCode(company));
        setSplitCommission(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [company, masId, shipId, setRevReq]);

  // ===========================================================================
  // BLOCK OEC.11C — EFFECT: Auto-set $0 Insurance from Notes + Terms
  // ===========================================================================
  useEffect(() => {
    if (zeroInsuranceManualRef.current) {
      return;
    }
    const next = inferZeroInsurance({ notesLines: notes, termsText: termsValue });
    setZeroInsurance(next);
  }, [notes, termsValue]);

  // ===========================================================================
  // BLOCK OEC.12 — EFFECTS: ZIP lookup (ZIP/company -> customers)
  // ===========================================================================
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLookupError(null);

      if (!zipForSearch || zipForSearch.length < 3) {
        if (!cancelled) {
          setCustomers([]);
          setSelectedCustomer('');
          setAddresses([]);
          setSelectedAddressIndex(null);
          setBillTo(null);
          setNotes([]);
          setSalesReps({ rep: [], mngr: [], tsr: [] });
          setFrtAccount('');
          setContractCode('');
          setContractNo('');
          setContractRows([]);
        }
        return;
      }

      if (!cancelled) {
        setFrtAccount('');
        setContractCode('');
        setContractNo('');
        setContractRows([]);
        setLookupLoading(true);
      }

      try {
        const rows = await getCustomersByZip({ company, zip: zipForSearch });
        if (!cancelled) {
          const nextCustomers = sanitizeTmsLookupRows(company, rows, (row) => {
            // Multiple street addresses belong in the Street list, not repeated Customer rows.
            const customerNo = String(row?.customerNo ?? '')
              .trim()
              .toUpperCase();
            const shipToName = String(row?.shipToName ?? '')
              .trim()
              .toUpperCase();
            return [customerNo, shipToName].join('::');
          });

          setCustomers(nextCustomers);

          if (shouldFocusCustomerListRef.current) {
            shouldFocusCustomerListRef.current = false;
            if (nextCustomers.length > 0) {
              setCustomerListFocusNonce((v) => v + 1);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          shouldFocusCustomerListRef.current = false;
          const status = err?.response?.status;
          setLookupError(status ? `Customer lookup failed (${status})` : 'Customer lookup failed.');
        }
      } finally {
        if (!cancelled) {
          setLookupLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [company, zipForSearch]);

  useEffect(() => {
    clearShipToSearch();
  }, [clearShipToSearch, company]);

  useEffect(() => {
    // Once TMS ShipTo P is active, ZIP becomes the patient destination ZIP.
    // Keep the customer ZIP lookup context frozen until the user leaves P mode.
    if (directShipPatientActive) {
      return undefined;
    }

    const val = normalizePostal5(zip);
    if (addressDisplayZipRef.current === val) {
      return undefined;
    }
    addressDisplayZipRef.current = null;

    if (val.length < 3) {
      setZipForSearch(val);
      return undefined;
    }

    const t = setTimeout(() => setZipForSearch(val), 1000);
    return () => clearTimeout(t);
  }, [directShipPatientActive, zip]);

  // ===========================================================================
  // BLOCK OEC.13A — EFFECT: Load part options when company changes
  // ===========================================================================
  useEffect(() => {
    const run = async () => {
      setPartError(null);
      setPartOptions([]);
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);

      setPartNo('');
      setPartInput('');
      setUom('');
      setPrice('');
      setPriceAutoSource('none');

      try {
        setPartLoading(true);

        const rows = await getPartList({ company });
        setPartOptions(rows.map((r) => ({ label: r.partNo, value: r.partNo })));
      } catch (err) {
        const status = err?.response?.status;
        setPartError(status ? `Unable to load parts (${status})` : 'Unable to load parts.');
      } finally {
        setPartLoading(false);
      }
    };

    run();
  }, [company]);

  // ===========================================================================
  // BLOCK OEC.13B — EFFECT: Load Terms list (SGR.Terms) for dropdown
  // ===========================================================================
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const rows = await getTerms();

        if (cancelled) {
          return;
        }

        const opts = (rows || [])
          .map((r) => {
            const code = String(r?.term_code ?? r?.termsCode ?? '').trim();
            const desc = String(r?.description ?? r?.termsDescription ?? '').trim();
            return { value: code, label: desc };
          })
          .filter((o) => o.value && o.label);

        setTermsOptions(opts);

        setTermsValue((prev) => {
          const p = String(prev ?? '').trim();
          if (!p) {
            return '';
          }
          return opts.some((o) => o.value === p) ? p : '';
        });
      } catch {
        if (!cancelled) {
          setTermsOptions([]);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  // ===========================================================================
  // BLOCK OEC.13C — EFFECT: Auto-select Terms from lastOrder.termsCode
  // ===========================================================================
  useEffect(() => {
    const code = String(preferredTermsCode ?? '').trim();
    if (!code) {
      return;
    }

    const exists = termsOptions.some((o) => String(o.value) === code);
    if (!exists) {
      return;
    }

    setTermsValue((prev) => {
      const p = String(prev ?? '').trim();

      if (p && termsOptions.some((o) => String(o.value) === p)) {
        return p;
      }

      return code;
    });
  }, [preferredTermsCode, termsOptions]);

  // ===========================================================================
  // BLOCK OEC.14 — EFFECT: Special order popup behavior
  // ===========================================================================
  useEffect(() => {
    const note = (specialNote || '').trim();

    if (!note) {
      setSpecialOpen(false);
      setSpecialAck(false);
      return;
    }

    if (!specialAck) {
      setSpecialOpen(true);
      setSpecialAck(true);
    }
  }, [specialNote, specialAck]);

  // ===========================================================================
  // BLOCK OEC.15B — EFFECT: Address-click driven FOB lookup (cached)
  // ---------------------------------------------------------------------------
  // Rules / Guards:
  //  - Requires (company + custNo + shipTo)
  //  - Do nothing if user is editing freight account
  //  - Do nothing if contract wants free freight
  //  - Do nothing if manual freight override was triggered
  //
  // Behavior:
  //  - Fetch most recent FOB from backend (cached).
  //  - If blank FOB => clear acct; if currently FC, downgrade to PPA.
  //  - If FOB is invalid for UPS/FDX => force PPA + clear acct; stop.
  //  - If valid => set frtAccount and align carrier if FOB indicates it.
  // ===========================================================================
  useEffect(() => {
    let cancelled = false;

    const custNo = String(lastSelectedAddress?.customerNo || '').trim();
    const shipTo = String(lastSelectedAddress?.shipToCode || '').trim();

    const run = async () => {
      if (!company || !custNo || !shipTo || !freightAccountsReady) {
        return;
      }

      if (frtAccountUserEditing) {
        return;
      }

      if (contractWantsFree) {
        return;
      }

      if (manualFreightOverrideRef.current) {
        return;
      }

      const rows = Array.isArray(existingFreightAccounts) ? existingFreightAccounts : [];
      const shipRow = shipTo
        ? rows.find((r) => String(r?.shipToCode ?? '').trim() === shipTo)
        : null;
      const customerRow = rows.find((r) => String(r?.shipToCode ?? '').trim() === '');

      // CustShipAcctNo is authoritative over FOB history. If a matching row exists,
      // even with accountno='', do not fall back to ARInvoiceHistoryHeader.
      if (shipRow || customerRow) {
        return;
      }

      try {
        const fobValueRaw = await getMostRecentFob({
          company,
          customerNo: custNo,
          shipToCode: shipTo,
        });

        if (
          cancelled ||
          frtAccountUserEditing ||
          frtAccountUserTouchedRef.current ||
          manualFreightOverrideRef.current
        ) {
          return;
        }

        const fobValue =
          typeof fobValueRaw === 'string'
            ? fobValueRaw.trim()
            : String(fobValueRaw?.fob ?? '').trim();

        // No FOB on file => clear acct; if we were FC, downgrade to PPA.
        if (!fobValue) {
          setFrtAccount('');
          setFrtPay((prev) => (prev === 'FC' ? 'PPA' : prev));
          return;
        }

        // ----------------------------------------------------------------------------
        // BLOCK OE.FOB.AUTO_PPA.0 — Invalid FOB ⇒ force PPA + clear acct (UPS/FDX only)
        // ----------------------------------------------------------------------------
        // Rule:
        //  - Only enforce strict validation for UPS/FDX
        //  - Prefer inferred carrier from FOB if available; fallback to current carrier
        //  - If invalid, force PPA and STOP (do not set frtAccount)
        // ----------------------------------------------------------------------------
        const carrierFromFob = inferCarrierFromFob(fobValue);
        const carrierUpper = String(carrierFromFob || carrierRef.current || '')
          .trim()
          .toUpperCase();
        const strict = carrierUpper === 'UPS' || carrierUpper === 'FDX';

        if (strict) {
          const ok = isValidFreightAccountForCarrier({
            carrier: carrierUpper,
            accountNo: fobValue,
          });

          if (!ok) {
            setFrtPay('PPA');
            setFrtAccount('');
            return;
          }
        }

        // Valid FOB => apply it
        setFrtAccount(fobValue);

        // Align carrier selection with FOB, if it clearly indicates one
        if (carrierFromFob === 'UPS') {
          setCarrier('UPS');
        } else if (carrierFromFob === 'FDX') {
          setCarrier('FDX');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching most recent FOB:', err);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    company,
    lastSelectedAddress,
    frtAccountUserEditing,
    contractWantsFree,
    existingFreightAccounts,
    freightAccountsReady,
  ]);

  // ===========================================================================
  // BLOCK OEC.15D — EFFECT: If FreeFrt => force FedEx (allow USPS)
  // ===========================================================================
  useEffect(() => {
    if (frtPay !== 'Free') {
      return;
    }

    // In TMS ShipTo P mode, keep FreeFrt locked but allow user-selected carriers.
    if (directShipFreightDefaults) {
      return;
    }

    const targetCarrier = directShipFreightDefaults?.carrier || 'FDX';
    if (carrier !== targetCarrier && carrier !== 'USPS') {
      setCarrier(targetCarrier);
    }
  }, [frtPay, carrier, directShipFreightDefaults]);

  // ===========================================================================
  // BLOCK OEC.15D2 — EFFECT: If FreeFrt => prefer FedEx Ground ONCE
  // ===========================================================================
  useEffect(() => {
    if (frtPay !== 'Free') {
      return;
    }

    if (!Array.isArray(shipViaList) || shipViaList.length === 0) {
      return;
    }

    if (shipVia && shipViaList.some((x) => x.code === shipVia)) {
      return;
    }

    const ground =
      directShipFreightDefaults?.carrier === 'USPS'
        ? shipViaList.find((x) => /USPSG/i.test(String(x.code))) || shipViaList[0]
        : shipViaList.find((x) => /FDX/i.test(String(x.code)) && /GND/i.test(String(x.code))) ||
          shipViaList.find((x) => /GND/i.test(String(x.code))) ||
          shipViaList[0];

    if (ground?.code) {
      setShipVia(ground.code);
    }
  }, [frtPay, shipViaList, shipVia, directShipFreightDefaults]);

  // ===========================================================================
  // BLOCK OEC.15E — EFFECT: Freight payment normalization (NO BOUNCE)
  // ===========================================================================
  useEffect(() => {
    if (frtAccountUserEditing) {
      return;
    }

    if (frtPay === 'Free') {
      return;
    }

    const hasAcct = String(frtAccount ?? '').trim().length > 0;

    if (frtPay === 'PPA' && hasAcct) {
      setFrtPay('FC');
    }
  }, [frtPay, frtAccount, frtAccountUserEditing]);

  // ==========================================================================
  // BLOCK OEC.15E.1 — EFFECT: Close freight save dialog if not Collect
  // ==========================================================================
  useEffect(() => {
    if (freightSaveOpen && frtPay !== 'FC') {
      setFreightSaveOpen(false);
    }
  }, [freightSaveOpen, frtPay]);

  // ==========================================================================
  // BLOCK OEC.15E.2 — Close freight remove dialog if not Collect
  // ==========================================================================
  useEffect(() => {
    if (freightRemoveOpen && frtPay !== 'FC') {
      setFreightRemoveOpen(false);
    }
  }, [freightRemoveOpen, frtPay]);

  // ===========================================================================
  // BLOCK OEC.15F — EFFECT: Infer carrier from frtAccount when Collect (FC)
  // ===========================================================================
  useEffect(() => {
    if (frtPay !== 'FC') {
      return;
    }

    const acct = String(frtAccount ?? '').trim();
    if (!acct) {
      return;
    }

    const inferred = inferCarrierFromFob(acct);
    if (!inferred) {
      return;
    }

    if (inferred !== carrier) {
      setCarrier(inferred);
    }
  }, [frtPay, frtAccount, carrier]);

  // ===========================================================================
  // BLOCK OEC.16 — Handler: PO blur (non-blank + format + duplicate PO warning)
  // ===========================================================================
  const validatePoNumber = useCallback(
    async (candidatePo, options = {}) => {
      const trimmed = String(candidatePo ?? '').trim();
      const allowBlankAlert = options.allowBlankAlert !== false;

      if (!trimmed) {
        if (allowBlankAlert) {
          window.alert('Blank PO not allowed.');
        }
        return '';
      }

      if (!masId) {
        return trimmed;
      }

      try {
        if (masId && shipId) {
          const formatCheck = await checkPoFormatSimilarity({
            company,
            customerNo: masId,
            shipToCode: shipId,
            poNumber: trimmed,
          });

          const priorPoNumbers = Array.isArray(formatCheck?.historyPoNumbers)
            ? formatCheck.historyPoNumbers.filter((v) => String(v || '').trim())
            : [];
          const similarityScore = Number(formatCheck?.score ?? 100);

          if (priorPoNumbers.length > 0 && similarityScore < PO_FORMAT_WARNING_THRESHOLD) {
            const priorLines = priorPoNumbers.join('\n');
            const poHistoryLabel =
              priorPoNumbers.length === 1
                ? 'last available PO#'
                : `last ${priorPoNumbers.length} available PO#s`;
            showAlertDebounced(
              `PO# ${trimmed} does not match format of ${poHistoryLabel} for this customer:\n${priorLines}\nPlease verify that PO is for this customer.`,
              `fmt_po_${trimmed}`
            );
          }
        }

        const matches = await checkPoDuplicate({ company, customerNo: masId, poNumber: trimmed });
        if (matches.length > 0) {
          const lines = matches
            .map((m) => {
              const label = m.source === 'SO' ? 'Order Date' : 'Invoice Date';
              return `PO: ${m.customerPONo} - ${label}: ${m.date}`;
            })
            .join('\n');

          showAlertDebounced(
            `Duplicate PO found in System or Invoice History:\n${lines}\n**VERIFY THAT IT IS NOT A DUPLICATE!!**`,
            `dup_po_${trimmed}`
          );
        }
      } catch {
        // ok
      }

      return trimmed;
    },
    [company, masId, shipId, showAlertDebounced]
  );

  const handlePoBlur = useCallback(async () => {
    const trimmed = await validatePoNumber(poNumber);
    if (!trimmed) {
      return;
    }

    if (!masId) {
      setReceivingLine((prev) => {
        const idx = prev.indexOf(' PO#');
        const base = idx > 0 ? prev.slice(0, idx) : prev;
        return `${base} PO#${trimmed}`;
      });
      return;
    }

    setReceivingLine((prev) => {
      const idx = prev.indexOf(' PO#');
      const base = idx > 0 ? prev.slice(0, idx) : prev;
      return `${base} PO#${trimmed}`;
    });
  }, [masId, poNumber, validatePoNumber]);

  const handleAcceptProductQuote = useCallback(() => {
    const nextPrice = productQuote?.discountPrice;
    if (nextPrice == null || nextPrice === '') {
      clearProductQuote();
      requestAnimationFrame(() => focusQty());
      return;
    }

    setPrice(formatMoneyInput(nextPrice));
    setPriceAutoSource('product_quote');
    clearProductQuote();
    requestAnimationFrame(() => focusQty());
  }, [clearProductQuote, focusQty, productQuote?.discountPrice]);

  const handleDeclineProductQuote = useCallback(() => {
    clearProductQuote();
    requestAnimationFrame(() => focusQty());
  }, [clearProductQuote, focusQty]);

  // ===========================================================================
  // BLOCK OEC.17 — PART LOOKUP HELPERS
  // ===========================================================================
  const norm = useCallback(
    (s) =>
      String(s ?? '')
        .trim()
        .toUpperCase(),
    []
  );

  const findClosestPartOption = useCallback(
    (options, typed) => {
      const t = norm(typed);
      if (!t) {
        return null;
      }

      let hit =
        options.find((o) => norm(o.value) === t) || options.find((o) => norm(o.label) === t);
      if (hit) {
        return hit;
      }

      hit =
        options.find((o) => norm(o.value).startsWith(t)) ||
        options.find((o) => norm(o.label).startsWith(t));
      if (hit) {
        return hit;
      }

      hit =
        options.find((o) => norm(o.value).includes(t)) ||
        options.find((o) => norm(o.label).includes(t));

      return hit || null;
    },
    [norm]
  );

  // ===========================================================================
  // BLOCK OEC.18 — Handler: Part lookup
  // ===========================================================================
  const handlePartLookup = useCallback(
    async (selectedPart, options = {}) => {
      const rawPart = (selectedPart || partNo || '').trim();

      setPartError(null);
      setContractRows([]);
      setLastPriceRow(null);
      setSpecialNote(null);

      const isTmsOrder = isTmsCompanyCode(company);
      const shouldAutoFillPrice = !isTmsOrder;

      // If we're restoring from a removed picking-sheet line, preserve the entry price.
      if (!options?.preserveEntryPrice) {
        setPrice(isTmsOrder ? '0.00' : '');
        setPriceAutoSource('none');
      }

      // -----------------------------------------------------------------------
      // BLOCK OEC.SPEC.RESET.ON_LOOKUP.0 — Clear special order flags per lookup
      // Prevents stale DS/MO/BL flags & Customer_Note from leaking to next part.
      // -----------------------------------------------------------------------
      setSpecOrderBo(false);
      setSpecOrderMo(false);
      setSpecOrderDs(false);
      setSpecOrderBl(false);
      setSpecOrderCustomerNote('');

      // -----------------------------------------------------------------------
      // BLOCK OEC.FRT.RESET.ON_LOOKUP.0 — Reset freight autopilot guards per lookup
      // - New part should re-derive freight rules unless user touches fields again.
      // -----------------------------------------------------------------------
      resetFreightAutopilotGuards();

      setTermsValue('');
      setPreferredTermsCode('');
      setPriceWarningText('');
      zeroInsuranceManualRef.current = false;

      if (!rawPart) {
        setPartError('Part number is required.');
        return;
      }
      if (!masId || !shipId) {
        setPartError('Select a MAS ID and Ship ID before choosing a part.');
        return;
      }

      try {
        setPartLoading(true);

        const data = await getPartInfo({
          company,
          customerNo: masId,
          shipToCode: shipId,
          item: rawPart,
        });

        const resolvedCode = data?.resolvedItemCode || rawPart;
        setPartNo(resolvedCode);

        const contracts = toArray(data?.contracts);
        setContractRows(contracts);

        const last = data?.lastOrder || null;

        const codeFromLast = String(last?.termsCode ?? '').trim();
        setPreferredTermsCode(codeFromLast);

        if (codeFromLast && termsOptions.some((o) => String(o.value) === codeFromLast)) {
          setTermsValue((prev) => {
            const p = String(prev ?? '').trim();
            return p || codeFromLast;
          });
        }

        // Term fallback handled after contract resolution below

        // ============================================================================
        // BLOCK OEC.18A — Price resolution (VBA parity)
        // Rules:
        //  1) If last order exists => show in LastPricePanel + set Price from last
        //  2) Else if contract exists => set Price from contract tier (tier else P1)
        //  3) Else leave Price blank
        //  4) If BOTH exist and tier price != last price => popup warning
        //  5) If last price not in ANY tier price => show mismatch warning
        // ============================================================================
        // Always reset last-price UI first; we only set it when a real last order exists.
        setLastPriceRow(null);

        // --- Helpers (local) ---
        const toNum = (v) => {
          if (v == null) {
            return null;
          }

          // parseMoneyNumber already handles strings like "$165.00"
          const n = parseMoneyNumber(v);
          return Number.isFinite(n) ? n : null;
        };

        const sameMoney = (a, b) => {
          if (a == null || b == null) {
            return false;
          }
          return Math.abs(a - b) < 0.0001;
        };

        // --- Resolve contract tier price (if any) ---
        const contract = contracts.length > 0 ? contracts[0] : null;

        // BLOCK OEC.18A.1 — Determine tier-based price
        // Ensure we properly identify when tier is truly specified (1-4 only)
        // Handle null, undefined, "", whitespace, 0, NaN, etc. as "not specified"
        const tierRaw = contract?.tier;
        const tierNum = tierRaw != null && String(tierRaw).trim() !== '' ? Number(tierRaw) : 0;
        const hasTierSpecified = tierNum === 1 || tierNum === 2 || tierNum === 3 || tierNum === 4;

        let tierVal = null;
        if (tierNum === 1) {
          tierVal = contract?.price1;
        } else if (tierNum === 2) {
          tierVal = contract?.price2;
        } else if (tierNum === 3) {
          tierVal = contract?.price3;
        } else if (tierNum === 4) {
          tierVal = contract?.price4;
        }

        // BLOCK OEC.18A.1A — Contract price for auto-fill
        // When tier specified: use that tier price
        // When tier NOT specified: fallback to price1
        const contractTierPriceNum =
          toNum(tierVal) != null ? toNum(tierVal) : toNum(contract?.price1);

        const tierListNums = [
          toNum(contract?.price1),
          toNum(contract?.price2),
          toNum(contract?.price3),
          toNum(contract?.price4),
        ].filter((x) => x != null);

        // Fallback terms: contract, then NET 30
        const contractTermsCode = String(contract?.termsCode ?? contract?.terms ?? '').trim();
        if (!codeFromLast && contractTermsCode) {
          const match = termsOptions.find((o) => String(o.value) === contractTermsCode);
          if (match) {
            setTermsValue((prev) => {
              const p = String(prev ?? '').trim();
              return p || contractTermsCode;
            });
          }
        }

        if (!codeFromLast && !contractTermsCode) {
          const net30 = termsOptions.find((o) =>
            /net\s*30/i.test(`${o.label || ''} ${o.value || ''}`)
          );
          if (net30) {
            setTermsValue((prev) => {
              const p = String(prev ?? '').trim();
              return p || String(net30.value);
            });
          }
        }

        // --- Resolve last order price (if any) ---
        const lastPriceNum = toNum(last?.unitPrice);

        // BLOCK OEC.18A.2 — Last order price (if exists, set price and show in panel)
        if (last && lastPriceNum != null) {
          setLastPriceRow({
            invoiceDate: last.invoiceDate || null,
            itemCode: last.itemCode || resolvedCode,
            unitPrice: last.unitPrice ?? null,
            terms: last.termsDescription || last.termsCode || '',
          });

          if (!options?.preserveEntryPrice && shouldAutoFillPrice) {
            setPrice(formatMoneyInput(last.unitPrice));
            setPriceAutoSource('last_order');
          }
        }

        // BLOCK OEC.18A.3 — Contract fallback (no last order, use tier price)
        if (lastPriceNum == null && contractTierPriceNum != null) {
          if (!options?.preserveEntryPrice && shouldAutoFillPrice) {
            setPrice(formatMoneyInput(contractTierPriceNum));
            setPriceAutoSource('contract');
          }
        }

        // BLOCK OEC.18A.4 — Mismatch popup (only when tier IS specified)
        // Rules:
        //  - If tier IS specified (1-4): popup if last price doesn't match that tier
        //  - If tier NOT specified: no popup (warning text only, handled below)
        if (lastPriceNum != null && hasTierSpecified && contractTierPriceNum != null) {
          if (!sameMoney(lastPriceNum, contractTierPriceNum)) {
            window.alert(
              `Price mismatch:\n` +
                `Last price: ${formatUSD(lastPriceNum)}\n` +
                `Contract tier ${tierNum} price: ${formatUSD(contractTierPriceNum)}\n\n` +
                `Verify pricing before proceeding.`
            );
          }
        }

        // BLOCK OEC.18A.5 — Warning if last price not in any contract tier
        // This applies regardless of whether tier is specified
        if (lastPriceNum != null && contract && tierListNums.length > 0) {
          const foundInTiers = tierListNums.some((n) => sameMoney(n, lastPriceNum));
          setPriceWarningText(foundInTiers ? '' : 'Pricing does not match contract pricing.');
        } else {
          setPriceWarningText('');
        }

        // ============================================================================
        // BLOCK OEC.18B — Apply phone / confirmTo defaults from last order (safe)
        // Notes:
        //  - Should run whenever last exists (even if last.unitPrice is null)
        // ============================================================================
        if (last) {
          if (!phone && last.comment) {
            setPhone(last.comment);
          }

          // ConfirmTo is always defaulted to "Purchasing" (user can override via dropdown)
        }
        const uomFromLast =
          last?.unitOfMeasure && String(last.unitOfMeasure).trim()
            ? String(last.unitOfMeasure).trim()
            : '';

        const uomFromDefault = data?.defaultSalesUOM ? String(data.defaultSalesUOM).trim() : '';
        const resolvedUom = uomFromLast || uomFromDefault;

        if (resolvedUom) {
          setUom(resolvedUom);
        }

        if (data?.specialOrder) {
          const type = String(data.specialOrder.type || '').toUpperCase();
          const intNote = data.specialOrder.internalNote || '';

          let header = '';
          switch (type) {
            case 'BO':
              header = '*****BackOrdered Item******';
              break;
            case 'MO':
              header = '*****Made To Order Item******';
              break;
            case 'DS':
              header = '*****Direct Ship from External Vendor *******';
              break;
            case 'BL':
              header = '*****Bill Only PO *********';
              break;
            default:
              break;
          }

          // OEC.PART_LOOKUP.SPEC.0 — set exact spec type + capture customer note
          setSpecOrderBo(false);
          setSpecOrderMo(false);
          setSpecOrderDs(false);
          setSpecOrderBl(false);

          if (type === 'BO') {
            setSpecOrderBo(true);
          } else if (type === 'MO') {
            setSpecOrderMo(true);
          } else if (type === 'DS') {
            setSpecOrderDs(true);
          } else if (type === 'BL') {
            setSpecOrderBl(true);
          }

          setSpecOrderCustomerNote(String(data?.specialOrder?.customerNote || '').trim());

          setSpecialAck(false);

          if (header || intNote) {
            const composed = `${header}${header ? '\n' : ''}${intNote}`;
            setSpecialNote(composed);
          }
        }

        const isTziQuoteEligible =
          String(company ?? '')
            .trim()
            .toLowerCase() === 'tzi';

        if (!isTziQuoteEligible || !resolvedCode || !masId || !shipId) {
          clearProductQuote();
        } else {
          try {
            const quote = await getTziProductQuote({
              customerNo: masId,
              shipToCode: shipId,
              partNo: resolvedCode,
            });

            if (
              quote?.found &&
              quote?.discountPrice != null &&
              String(quote.discountPrice).trim() !== ''
            ) {
              setProductQuote({
                name: String(quote?.name || quote?.mftPartNum || resolvedCode).trim(),
                mftPartNum: String(quote?.mftPartNum || '').trim(),
                dateEntered: quote?.dateEntered || null,
                discountPrice: quote?.discountPrice ?? null,
                quantity: quote?.quantity ?? null,
                outOfDate: Boolean(quote?.outOfDate),
              });
              setProductQuoteDefaultUsePrice(!quote?.outOfDate);
              setProductQuoteDialogOpen(true);
            } else {
              clearProductQuote();
            }
          } catch {
            clearProductQuote();
          }
        }
      } catch (err) {
        const status = err?.response?.status;
        setPartError(status ? `Part lookup failed (${status})` : 'Part lookup failed.');
      } finally {
        setPartLoading(false);
      }
    },
    [
      clearProductQuote,
      company,
      masId,
      shipId,
      partNo,
      phone,
      termsOptions,
      resetFreightAutopilotGuards,
    ]
  );

  // ===========================================================================
  // BLOCK OEC.19 — Derived UI state
  // ===========================================================================
  const disabled = lookupLoading || partLoading;

  const poReady = String(poNumber ?? '').trim().length > 0;
  const badge3Disabled =
    disabled ||
    !poReady ||
    !masId ||
    !shipId ||
    (directShipPatientActive && !directShipPatientComplete);
  const notesWithHoldHeader = useMemo(() => {
    const base = Array.isArray(notes) ? notes : [];
    return creditHold ? ['***** Credit Hold *****', ...base] : base;
  }, [creditHold, notes]);

  // ============================================================================
  // BLOCK OEC.RESET.CUSTOMER_CONTEXT.0
  // Purpose:
  //  - Clear MAS / ShipTo context after successful submit
  //  - Hide LastPrice panel
  //  - Reset customer-specific state (Excel parity)
  // ============================================================================
  const resetCustomerContext = useCallback(() => {
    // Invalidate in-flight lookups before clearing visible state.
    customerSelectRequestSeqRef.current += 1;
    clearShipToSearch();
    addressDisplayZipRef.current = null;
    shouldFocusCustomerListRef.current = false;
    requireManualStreetSelectionRef.current = false;
    setLookupLoading(false);
    setLookupError(null);
    // Core customer identifiers
    setMasId('');
    setShipId('');

    // Customer + address context
    setCustomers([]);
    setSelectedCustomer('');
    setAddresses([]);
    setSelectedAddressIndex(null);
    setLastSelectedAddress(null);

    // Display fields
    setCustomerName('');
    setShipAddress1('');
    setShipAddress2('');
    setDirectShipPatient(createEmptyDirectShipPatient());
    setDirectShipDialogOpen(false);
    clearProductQuote();

    // Location
    setCity('');
    setState('');
    setZip('');
    setZipForSearch('');

    // Notes / bill-to
    setBillTo(null);
    setNotes([]);

    // Last price panel + contract context
    setLastPriceRow(null);
    setContractRows([]);
    setContractCode('');
    setContractNo('');

    // Reset freight/account context
    resetFreightAutopilotGuards();

    setFrtAccountUserEditing(false);
    setFrtAccount('');
    setCarrier('FDX');
    setFrtPay('FC');
    setShipVia('');
    setShipViaInitialized(false);
    setExistingFreightAccounts([]);
    setFreightReloadNonce((n) => n + 1);
    setFreightSaveOpen(false);
    setFreightSaveScope('shipto');
    setFreightSaveError(null);
    setFreightSaveBusy(false);

    // Sales reps
    setSalesReps({ rep: [], mngr: [], tsr: [] });
    setRepSlots([]);

    // Flags
    setSplitCommission(false);

    setSpecOrderBo(false);
    setSpecOrderMo(false);
    setSpecOrderDs(false);
    setSpecOrderBl(false);
    setSpecOrderCustomerNote('');

    setHouseAccount(isTmsCompanyCode(company));
    setOmitCommentCodes(isTmsCompanyCode(company));
    setCustomCommentsText('');
    setCreditHold(false);
    setInternationalSale(false);
    setZeroInsurance(false);
  }, [clearProductQuote, clearShipToSearch, company, resetFreightAutopilotGuards]);

  // ==========================================================================
  // BLOCK OEC.RESET.ALL — Reset entire form to initial state
  // Purpose: Clear all UI and controller state back to initial defaults.
  // Exposed so the page can provide a single "Clear Form" action.
  // ==========================================================================
  const resetAll = useCallback(() => {
    // Reset company to default
    setCompany(defaultCompany);

    // Core customer identifiers + related context
    resetCustomerContext();

    // Clear location + header fields
    setZip('');
    setZipForSearch('');
    setCity('');
    setState('');

    setPoNumber('');
    setConfirmTo('Purchasing');
    setPhone('');
    setReceivingLine('');
    setDirectShipPatient(createEmptyDirectShipPatient());
    setDirectShipDialogOpen(false);
    clearProductQuote();

    // Clear part/price/contract state
    setPartNo('');
    setPartInput('');
    setQty('');
    setUom('');
    setPrice('');
    setContractCode('');
    setContractNo('');
    setLastPriceRow(null);
    setSpecialNote(null);

    // Freight/account defaults
    resetFreightAutopilotGuards();

    zeroInsuranceManualRef.current = false;
    setFrtAccountUserEditing(false);
    setFrtAccount('');
    setCarrier('FDX');
    setFrtPay('FC');
    setShipVia('');
    setShipViaInitialized(false);
    setExistingFreightAccounts([]);
    setFreightReloadNonce((n) => n + 1);
    setFreightSaveOpen(false);
    setFreightSaveScope('shipto');
    setFreightSaveError(null);
    setFreightSaveBusy(false);

    // Reps / flags
    setSalesReps({ rep: [], mngr: [], tsr: [] });
    setRepSlots([]);
    setSplitCommission(false);

    setSpecOrderBo(false);
    setSpecOrderMo(false);
    setSpecOrderDs(false);
    setSpecOrderBl(false);
    setSpecOrderCustomerNote('');

    setHouseAccount(isTmsCompanyCode(defaultCompany));
    setOmitCommentCodes(isTmsCompanyCode(defaultCompany));
    setCustomCommentsText('');
    setCreditHold(false);
    setInternationalSale(false);
    setZeroInsurance(false);

    // Other UI bits
    setPartError(null);
    setLookupError(null);
    setLookupLoading(false);
  }, [clearProductQuote, defaultCompany, resetCustomerContext, resetFreightAutopilotGuards]);

  // ===========================================================================
  // BLOCK OEC.20 — Return controller surface
  // ===========================================================================
  return {
    // values
    company,
    zip,
    zipForSearch,
    state,
    city,
    masId,
    shipId,
    selectedAddressShipId,
    customerName,
    shipAddress1,
    shipAddress2,
    directShipPatient,
    directShipPatientActive,
    directShipPatientComplete,
    directShipDialogOpen,
    directShipStudies,
    directShipStudiesLoading,
    directShipStudiesError,
    user,
    customers,
    selectedCustomer,
    addresses,
    selectedAddressIndex,
    lookupLoading,
    lookupError,
    billTo,
    notes: notesWithHoldHeader,
    creditHold,
    revReq,

    shipToSearchRows,
    shipToSearchLoading,
    shipToSearchError,
    customerListFocusNonce,

    receivingLine,
    confirmTo,
    confirmToOptions,
    confirmToLoading,
    phone,
    poNumber,

    specialOpen,
    specialNote,

    frtAccountUserEditing,
    carrier,
    frtPay,
    shipVia,
    shipViaList,
    shipEtaMap,
    frtAccount,
    existingFreightAccounts,
    freightAccountsReady,
    freightSaveOpen,
    freightSaveScope,
    freightSaveError,
    freightSaveBusy,
    freightSavePreviewRows,
    freightAccountsLoading,
    freightLoadError,
    freightSaveContext,
    retryFreightAccounts,

    freightRemoveOpen,
    freightRemoveScope,
    freightRemoveError,
    freightRemoveBusy,

    contractCode,
    contractNo,
    contractRows,

    splitCommission,

    // Special order flags + note (used by PartPanel + submit + Sugar note)
    specOrderBo,
    specOrderMo,
    specOrderDs,
    specOrderBl,
    specOrderCustomerNote,

    houseAccount,
    omitCommentCodes,
    customCommentsText,
    internationalSale,
    zeroInsurance,

    partNo,
    partInput,
    partOptions,
    partLoading,
    partError,
    lastPriceRow,
    uom,
    qty,
    price,
    priceAutoSource,
    priceWarningText,
    productQuote,
    productQuoteDialogOpen,
    productQuoteDefaultUsePrice,
    handleAcceptProductQuote,
    handleDeclineProductQuote,
    acceptProductQuote: handleAcceptProductQuote,
    declineProductQuote: handleDeclineProductQuote,

    // reps (single source of truth for submit)
    salesReps,
    houseAccountReps,

    // terms
    termsValue,
    termsOptions,

    // derived
    orderTotalText,
    disabled,
    badge3Disabled,

    // reps
    repSlots,
    legacyRepFields,

    // refs
    zipRef,
    poRef,
    partRef,
    qtyRef,

    // actions / setters
    resetCustomerContext,
    resetAll,
    openDirectShipPatientDialog,
    closeDirectShipPatientDialog,
    submitDirectShipPatient,
    handleShipToSearchSelect,
    searchShipToAddresses,
    clearShipToSearch,
    setZip: setZipNormalized,
    setCity,
    setState,

    setPoNumber,
    setConfirmTo,
    setPhone,
    setReceivingLine,
    loadConfirmToOptions,

    setPartNo,
    setPartInput,
    setQty,
    setUom,
    setPrice,

    setShipVia,
    setCarrier: handleCarrierChange,
    setFrtPay: handleFrtPayChange,
    setFrtAccount,
    onFrtAccountUserEdit: handleFrtAccountUserEdit,
    handleFrtAccountBlur,
    closeFreightSaveDialog,
    submitFreightSave,
    setFreightSaveScope,
    setFreightSaveOpen,
    setFreightSaveError,

    closeFreightRemoveDialog,
    submitFreightRemove,
    setFreightRemoveScope,

    setContractCode,
    setContractNo,

    setSplitCommission,
    setRevReq,

    // Special order flags (must be writable from PartPanel)
    setSpecOrderBo,
    setSpecOrderMo,
    setSpecOrderDs,
    setSpecOrderBl,
    setSpecOrderCustomerNote,

    setHouseAccount,
    setOmitCommentCodes,
    setCustomCommentsText,
    setInternationalSale,
    setZeroInsurance,

    onZeroInsuranceManual: () => {
      zeroInsuranceManualRef.current = true;
    },

    setTermsValue,

    // handlers
    handleCompanyChange,
    handleCustomerSelect,
    handleAddressClick,
    handleShipIdChange,
    handlePoBlur,
    handlePartLookup,
    findClosestPartOption,
    handleCarrierChange,
    handleFrtPayChange,

    // focus helpers
    focusPoNumber,
    focusPartNo,
    focusQty,

    // dialog close helper (keep explicit)
    closeSpecialDialog: () => setSpecialOpen(false),
  };
}
